import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode, CSSProperties } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, type ExportOption } from '../../components/AppToolbar';
import { ColFilterButton, passesColumnFilter, type ColumnFilterValue } from '../../components/ColumnFilterDropdown';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { FormTitle } from '../../components/FormTitle';
import { AiCombo } from '../../components/AiCombo';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm, useDeleteFlow } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { ReportDesignerFrame, type DesignerMode } from './ReportDesignerFrame';
import { BusyIcon } from '../../components/loading';

// ─────────────────────────────────────────────────────────────────────────────
// Custom Report Builder — flat-grid port of ALPACA's XAF
// "Custom Report Builder" (ViewID=CustomReportBuilder).
//
// Two tabs:
//   • Report Designer — the flat template grid (dbo.tbl_Public_CustomReportDesignerDetails).
//       Toolbar: Design · New · Edit · Delete · Preview · Refresh · Export to.
//       Edit opens a settings editor modal that mirrors ALPACA's
//       CustomReportBuilder_DetailView: TemplateID (read-only), TemplateName,
//       Custom Caption, Category + Report Type dropdowns, Comment, the report
//       flags, and the Form Report Permissions sub-grid.
//       New / Design / Preview (and the row's layout icon) replace the list with
//       the full-page <ReportDesignerFrame> — the DevExpress v20.1 Web Report
//       Designer / Viewer ported from Alpaca Studio's "Report Template Builder"
//       (2026-09-22, ADR-0014).  Delete = the audited soft-deactivate (Active = 0).
//   • Report Package — the package grid (dbo.ReportPackage).
//
// Endpoints:
//   GET  /api/v1/custom-report-templates                 → Report Designer grid
//   GET  /api/v1/custom-report-templates/lookups         → Category/Report Type options
//   GET  /api/v1/custom-report-templates/{id}            → editor detail
//   GET  /api/v1/custom-report-templates/{id}/form-permissions → permissions sub-grid
//   PUT  /api/v1/custom-report-templates/{id}            → update
//   DELETE /api/v1/custom-report-templates/{id}          → soft-deactivate (audited)
//   POST /api/v1/custom-report-templates/designer-session → designer iframe session
//   GET  /api/v1/report-packages                         → Report Package grid
// ─────────────────────────────────────────────────────────────────────────────

interface TemplateSummary {
  templateId:         number;
  templateName:       string | null;
  userDefinedName:    string | null;
  customCaption:      string | null;
  reportCategory:     string | null;
  reportType:         string | null;
  active:             boolean;
  finalReport:        boolean;
  showReportId:       boolean;
  waterMark:          boolean;
  allowMultipleJobId: boolean;
  isAdhoc:            boolean;
  sort:               number;
  comment:            string | null;
  lastUpdatedDate:    string | null;
  lastUpdatedBy:      string | null;
}

interface TemplateDetail {
  templateId:          number;
  templateName:        string | null;
  userDefinedName:     string | null;
  customCaption:       string | null;
  reportCategory:      string | null;
  reportType:          string | null;
  categoryOid:         string | null;
  reportTypeOid:       string | null;
  moduleName:          string | null;
  businessObjectName:  string | null;
  dataSourceQuery:     string | null;
  reportLayoutXml:     string | null;
  tableSchema:         string | null;
  normalColumn:        string | null;
  rowColumn:           string | null;
  uniqueColumn:        string | null;
  categoryId:          number;
  reportTypeId:        number;
  verticalColumnCount: number;
  active:              boolean;
  needSignOff:         boolean;
  finalReport:         boolean;
  showReportId:        boolean;
  waterMark:           boolean;
  cocSettings:         boolean;
  allowMultipleJobId:  boolean;
  isAdhoc:             boolean;
  sort:                number;
  comment:             string | null;
  userAccess:          string | null;
  lastUpdatedDate:     string | null;
  lastUpdatedBy:       string | null;
}

interface LookupOption { oid: string; name: string | null; }
interface FormPermissionRow { oid: string; formsOid: string | null; formName: string | null; userAccess: string | null; }

// One package in the grid (= the header row).
interface ReportPackageListRow {
  oid:           string;
  packageName:   string | null;
  customCaption: string | null;
  category:      string | null;
  reportType:    string | null;
  userAccess:    string | null;
  amended:       boolean;
  isActive:      boolean;
}
// One report inside a package.
interface ReportPackageItem {
  oid:           string;
  reportName:    string | null;
  customCaption: string | null;
  displayPage:   boolean;
  pageCount:     boolean;
  sort:          number;
}
// Full package (header + reports) — the edit dialog.
interface ReportPackageDetail {
  oid:              string;
  packageName:      string | null;
  customCaption:    string | null;
  categoryOid:      string | null;
  reportTypeOid:    string | null;
  clientOid:        string | null;
  clientName:       string | null;
  isActive:         boolean;
  eSign:            boolean;
  amended:          boolean;
  amendedText:      string | null;
  levelOneSigner:   string | null;
  levelTwoSigner:   string | null;
  levelThreeSigner: string | null;
  userAccess:       string | null;
  reports:          ReportPackageItem[];
}

function apiErr(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? (e instanceof Error ? e.message : fallback);
}

type Tab = 'designer' | 'package';

export default function CustomReportBuilderPage() {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { alert: showAlert, confirm: showConfirm } = useConfirm();   // centered popup (ALPACA-style)

  const [tab, setTab] = useState<Tab>('designer');

  // ── Report Designer (template grid) ──────────────────────────────────────
  const [list, setList]               = useState<TemplateSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listMs, setListMs]           = useState<number | null>(null);
  const [selected, setSelected]       = useState<Set<string | number>>(new Set());

  const loadList = useCallback(async () => {
    setListLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/custom-report-templates');
      setList((res.data as { data: TemplateSummary[] }).data ?? []);
    } catch (e) {
      notify.error(`Failed to load templates: ${apiErr(e, 'API error.')}`);
      setList([]);
    } finally {
      setListMs(performance.now() - t0);
      setListLoading(false);
    }
  }, [notify]);
  useEffect(() => { void loadList(); }, [loadList]);

  // ── Lookups (loaded once): Category, Report Type, Forms, Roles ────────────
  const [categories, setCategories]   = useState<LookupOption[]>([]);
  const [reportTypes, setReportTypes] = useState<LookupOption[]>([]);
  const [forms, setForms]             = useState<LookupOption[]>([]);
  const [roles, setRoles]             = useState<LookupOption[]>([]);
  const loadLookups = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/custom-report-templates/lookups');
      const d = (res.data as { data: {
        categories: LookupOption[]; reportTypes: LookupOption[];
        forms: LookupOption[]; roles: LookupOption[];
      } }).data;
      setCategories(d.categories ?? []);
      setReportTypes(d.reportTypes ?? []);
      setForms(d.forms ?? []);
      setRoles(d.roles ?? []);
    } catch { /* dropdowns simply show N/A only */ }
  }, []);
  useEffect(() => { void loadLookups(); }, [loadLookups]);
  // Resolve a "; "-joined Role-Oid list to readable role names (grid + dialog).
  const roleNames = useCallback((csv: string | null): string => {
    if (!csv) return '';
    const byId = new Map(roles.map(r => [r.oid.toLowerCase(), r.name ?? '']));
    return csv.split(';').map(s => s.trim()).filter(Boolean)
      .map(id => byId.get(id.toLowerCase()) || id).join(', ');
  }, [roles]);

  // ── Report Package grid ──────────────────────────────────────────────────
  const [pkgRows, setPkgRows]       = useState<ReportPackageListRow[]>([]);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgMs, setPkgMs]           = useState<number | null>(null);
  const [pkgLoaded, setPkgLoaded]   = useState(false);
  const [pkgSel, setPkgSel]         = useState<Set<string | number>>(new Set());

  const loadPackages = useCallback(async () => {
    setPkgLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/report-packages');
      setPkgRows((res.data as { data: ReportPackageListRow[] }).data ?? []);
      setPkgLoaded(true);
    } catch (e) {
      notify.error(`Failed to load report packages: ${apiErr(e, 'API error.')}`);
      setPkgRows([]);
    } finally {
      setPkgMs(performance.now() - t0);
      setPkgLoading(false);
    }
  }, [notify]);
  useEffect(() => { if (tab === 'package' && !pkgLoaded) void loadPackages(); }, [tab, pkgLoaded, loadPackages]);

  // Package dialog lookups (clients / employees / roles / report names) — lazy.
  const [pkgClients, setPkgClients]   = useState<LookupOption[]>([]);
  const [pkgEmployees, setPkgEmployees] = useState<LookupOption[]>([]);
  const [pkgRoles, setPkgRoles]       = useState<LookupOption[]>([]);
  const [pkgCategories, setPkgCategories] = useState<LookupOption[]>([]);
  const [pkgReportTypes, setPkgReportTypes] = useState<LookupOption[]>([]);
  const [pkgReportNames, setPkgReportNames] = useState<string[]>([]);
  const [pkgLookupsLoaded, setPkgLookupsLoaded] = useState(false);
  const loadPkgLookups = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/report-packages/lookups');
      const d = (res.data as { data: { clients: LookupOption[]; employees: LookupOption[]; roles: LookupOption[];
        categories: LookupOption[]; reportTypes: LookupOption[]; reportNames: string[] } }).data;
      setPkgClients(d.clients ?? []); setPkgEmployees(d.employees ?? []);
      setPkgRoles(d.roles ?? []); setPkgCategories(d.categories ?? []);
      setPkgReportTypes(d.reportTypes ?? []); setPkgReportNames(d.reportNames ?? []);
      setPkgLookupsLoaded(true);
    } catch { /* dialog falls back to N/A only */ }
  }, []);

  // Resolve a package's UserAccess (role-Oid csv) to role names for the grid.
  const pkgRoleNames = useCallback((csv: string | null): string => {
    if (!csv) return '';
    const byId = new Map(pkgRoles.map(r => [r.oid.toLowerCase(), r.name ?? '']));
    return csv.split(';').map(s => s.trim()).filter(Boolean).map(id => byId.get(id.toLowerCase()) || '').filter(Boolean).join('; ');
  }, [pkgRoles]);
  // Load package lookups when the package tab opens (so the grid can resolve roles).
  useEffect(() => { if (tab === 'package' && !pkgLookupsLoaded) void loadPkgLookups(); }, [tab, pkgLookupsLoaded, loadPkgLookups]);

  // ── Report Package edit dialog ───────────────────────────────────────────
  const [pkgEditOpen, setPkgEditOpen]   = useState(false);
  const [pkgDetail, setPkgDetail]       = useState<ReportPackageDetail | null>(null);
  const [pkgDetailLoading, setPkgDetailLoading] = useState(false);
  const [pkgSaving, setPkgSaving]       = useState(false);
  const [pkgRoleIds, setPkgRoleIds]     = useState<Set<string>>(new Set());
  const [pkgItemSel, setPkgItemSel]     = useState<Set<string>>(new Set());  // checked report rows in the sub-grid (ALPACA multi-select)
  const [pkgMax, setPkgMax] = useState(false);  // Report Package dialog maximize/restore (ALPACA header button)
  const [editorMax, setEditorMax] = useState(false);  // Custom Report Builder edit modal maximize/restore
  // Reports sub-grid sort + Excel-style column filters (click a column header to
  // sort; funnel opens the multi-select filter popup).
  const [pkgSort, setPkgSort] = useState<{ col: keyof ReportPackageItem; dir: 'asc' | 'desc' }>({ col: 'sort', dir: 'asc' });
  const [reportFilters, setReportFilters] = useState<Record<string, ColumnFilterValue>>({});
  const pkgSortBy = (col: keyof ReportPackageItem) =>
    setPkgSort(s => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  const pkgSortArrow = (col: keyof ReportPackageItem) =>
    pkgSort.col === col
      ? <i className={`ti ti-caret-${pkgSort.dir === 'asc' ? 'up' : 'down'}-filled crb-sort-ind`} />
      : <i className="ti ti-caret-up-down crb-sort-ind dim" />;
  const reportText = (r: ReportPackageItem, col: string) => {
    switch (col) {
      case 'reportName':    return r.reportName ?? '';
      case 'customCaption': return r.customCaption ?? '';
      case 'displayPage':   return r.displayPage ? 'Yes' : 'No';
      case 'pageCount':     return r.pageCount ? 'Yes' : 'No';
      case 'sort':          return String(r.sort);
      default:              return '';
    }
  };
  const pkgSortedReports = (reports: ReportPackageItem[]) => {
    const { col, dir } = pkgSort; const mul = dir === 'asc' ? 1 : -1;
    const shown = reports.filter(r => Object.entries(reportFilters).every(([k, sel]) => passesColumnFilter(sel, reportText(r, k))));
    return [...shown].sort((a, b) => {
      const av = a[col], bv = b[col];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
      if (typeof av === 'boolean' && typeof bv === 'boolean') return (av === bv ? 0 : av ? 1 : -1) * mul;
      return String(av ?? '').localeCompare(String(bv ?? '')) * mul;
    });
  };

  // Styled name-prompt modal (replaces the native window.prompt for New
  // Form / Category / Report Type — ALPACA-style popup over a blurred backdrop).
  // Category / Report Type also show a Description field (ALPACA's form).
  const [promptCfg, setPromptCfg]     = useState<{ title: string; label: string; withDescription: boolean } | null>(null);
  const [promptValue, setPromptValue] = useState('');
  const [promptDesc, setPromptDesc]   = useState('');
  const promptResolve = useRef<((v: { name: string; description: string } | null) => void) | null>(null);
  const askName = useCallback((title: string, label: string, withDescription = false) =>
    new Promise<{ name: string; description: string } | null>(resolve => {
      promptResolve.current = resolve;
      setPromptValue('');
      setPromptDesc('');
      setPromptCfg({ title, label, withDescription });
    }), []);
  const closePrompt = useCallback((val: { name: string; description: string } | null) => {
    setPromptCfg(null);
    const r = promptResolve.current; promptResolve.current = null;
    if (r) r(val);
  }, []);

  // Styled confirm modal (replaces the native window.confirm for Delete actions —
  // readable, centred over a blurred backdrop instead of the browser's top bar).
  const [confirmCfg, setConfirmCfg] = useState<{ title: string; message: string } | null>(null);
  const confirmResolve = useRef<((v: boolean) => void) | null>(null);
  const askConfirm = useCallback((title: string, message: string) =>
    new Promise<boolean>(resolve => {
      confirmResolve.current = resolve;
      setConfirmCfg({ title, message });
    }), []);
  const closeConfirm = useCallback((val: boolean) => {
    setConfirmCfg(null);
    const r = confirmResolve.current; confirmResolve.current = null;
    if (r) r(val);
  }, []);

  const blankPackage = (): ReportPackageDetail => ({
    oid: '', packageName: '', customCaption: '', categoryOid: null, reportTypeOid: null,
    clientOid: null, clientName: null, isActive: false, eSign: false,   // ALPACA: Is Active unchecked by default
    amended: false, amendedText: null, levelOneSigner: null, levelTwoSigner: null, levelThreeSigner: null,
    userAccess: null, reports: [],
  });

  const openPkgNew = useCallback(async () => {
    if (!pkgLookupsLoaded) await loadPkgLookups();
    setPkgDetail(blankPackage()); setPkgRoleIds(new Set()); setPkgItemSel(new Set()); setPkgEditOpen(true);
  }, [pkgLookupsLoaded, loadPkgLookups]);

  const openPkgEdit = useCallback(async (oid: string) => {
    if (!pkgLookupsLoaded) await loadPkgLookups();
    setPkgEditOpen(true); setPkgDetail(null); setPkgItemSel(new Set()); setPkgDetailLoading(true);
    try {
      const res = await api.get(`/api/v1/report-packages/${oid}`);
      const d = (res.data as { data: ReportPackageDetail }).data;
      setPkgDetail(d);
      setPkgRoleIds(new Set((d.userAccess ?? '').split(';').map(s => s.trim()).filter(Boolean)));
    } catch (e) {
      notify.error(`Failed to load package: ${apiErr(e, 'API error.')}`);
      setPkgEditOpen(false);
    } finally {
      setPkgDetailLoading(false);
    }
  }, [pkgLookupsLoaded, loadPkgLookups, notify]);

  const closePkgEdit = useCallback(() => { setPkgEditOpen(false); setPkgDetail(null); setPkgItemSel(new Set()); setPkgMax(false); }, []);

  const pkgField = useCallback(<K extends keyof ReportPackageDetail>(key: K, value: ReportPackageDetail[K]) => {
    setPkgDetail(d => (d ? { ...d, [key]: value } : d));
  }, []);

  // ── Client List "New" (ALPACA NwClient popup: Client Name + auto Unique ID) ──
  const [clientNewOpen, setClientNewOpen]     = useState(false);
  const [clientNewName, setClientNewName]     = useState('');
  const [clientNewSaving, setClientNewSaving] = useState(false);
  const openClientNew = useCallback(() => { setClientNewName(''); setClientNewOpen(true); }, []);
  const saveClientNew = useCallback(async () => {
    const name = clientNewName.trim();
    if (!name) { notify.warning('Client Name is required.'); return; }
    setClientNewSaving(true);
    try {
      const res = await api.post('/api/v1/report-packages/clients', { name });
      const data = (res.data as { data: { oid: string; uniqueId: number; name: string } }).data;
      await loadPkgLookups();
      pkgField('clientOid', data.oid);
      setClientNewOpen(false);
      void showAlert({ message: `Client "${data.name}" created (#${String(data.uniqueId).padStart(3, '0')}).`, tone: 'success' });
    } catch (e) {
      notify.error(`Could not create Client: ${apiErr(e, 'API error.')}`);
    } finally {
      setClientNewSaving(false);
    }
  }, [clientNewName, loadPkgLookups, pkgField, notify, showAlert]);

  // Sub-grid Add / Remove report rows.
  // Add reports — opens a searchable multi-select picker (ALPACA parity); the
  // checked report templates are appended as new report rows.
  const [pkgPickerOpen, setPkgPickerOpen]     = useState(false);
  const [pkgPickerSearch, setPkgPickerSearch] = useState('');
  const [pkgPickerSel, setPkgPickerSel]       = useState<Set<string>>(new Set());
  const openPkgPicker = useCallback(() => {
    // ALPACA validates Package Name before letting you add reports.
    if (!pkgDetail?.packageName?.trim()) { notify.warning('Please enter Package Name.'); return; }
    // ALPACA also keys reports off the Client — but a Client cannot be created from
    // here and the Client master may be empty, so only enforce it when clients exist.
    if (pkgClients.length > 0 && !pkgDetail?.clientOid) { notify.warning('Please select a Client.'); return; }
    setPkgPickerSearch(''); setPkgPickerSel(new Set()); setPkgPickerOpen(true);
  }, [pkgDetail, pkgClients, notify]);
  const togglePicker = useCallback((name: string) => {
    setPkgPickerSel(prev => { const n = new Set(prev); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  }, []);
  const pkgPickerConfirm = useCallback(() => {
    const names = Array.from(pkgPickerSel);
    setPkgDetail(d => {
      if (!d || names.length === 0) return d;
      const t = Date.now();
      // ALPACA adds each report with sort = 0 (the user sets the order afterwards).
      const newItems: ReportPackageItem[] = names.map((nm, i) => ({
        oid: `new-${t}-${i}`, reportName: nm, customCaption: '', displayPage: false, pageCount: false, sort: 0,
      }));
      return { ...d, reports: [...d.reports, ...newItems] };
    });
    setPkgPickerOpen(false);
  }, [pkgPickerSel]);
  const pkgRemoveReport = useCallback(() => {
    if (pkgItemSel.size === 0) { notify.warning('Select report row(s) to remove.'); return; }
    setPkgDetail(d => (d ? { ...d, reports: d.reports.filter(r => !pkgItemSel.has(r.oid)) } : d));
    setPkgItemSel(new Set());
  }, [pkgItemSel, notify]);
  const pkgItemField = useCallback(<K extends keyof ReportPackageItem>(oid: string, key: K, value: ReportPackageItem[K]) => {
    setPkgDetail(d => (d ? { ...d, reports: d.reports.map(r => r.oid === oid ? { ...r, [key]: value } : r) } : d));
  }, []);
  // Sub-grid Refresh — reload the package's report rows from the saved state.
  const pkgRefreshReports = useCallback(async () => {
    if (!pkgDetail?.oid) { setPkgDetail(d => (d ? { ...d, reports: [] } : d)); setPkgItemSel(new Set()); return; }
    try {
      const res = await api.get(`/api/v1/report-packages/${pkgDetail.oid}`);
      const d = (res.data as { data: ReportPackageDetail }).data;
      setPkgDetail(p => (p ? { ...p, reports: d.reports } : p));
      setPkgItemSel(new Set());
    } catch (e) { notify.error(`Refresh failed: ${apiErr(e, 'API error.')}`); }
  }, [pkgDetail, notify]);

  const savePackage = useCallback(async () => {
    if (!pkgDetail) return;
    if (!pkgDetail.packageName?.trim()) { notify.warning('Package Name is required.'); return; }
    const body = {
      packageName: pkgDetail.packageName, customCaption: pkgDetail.customCaption,
      categoryOid: pkgDetail.categoryOid, reportTypeOid: pkgDetail.reportTypeOid, clientOid: pkgDetail.clientOid,
      isActive: pkgDetail.isActive, eSign: pkgDetail.eSign, amended: pkgDetail.amended, amendedText: pkgDetail.amendedText,
      levelOneSigner: pkgDetail.levelOneSigner, levelTwoSigner: pkgDetail.levelTwoSigner, levelThreeSigner: pkgDetail.levelThreeSigner,
      userAccess: Array.from(pkgRoleIds).join('; '),
      reports: pkgDetail.reports.map(r => ({ reportName: r.reportName, customCaption: r.customCaption, displayPage: r.displayPage, pageCount: r.pageCount, sort: r.sort })),
    };
    setPkgSaving(true);
    try {
      if (pkgDetail.oid) await api.put(`/api/v1/report-packages/${pkgDetail.oid}`, body);
      else await api.post('/api/v1/report-packages', body);
      await loadPackages();
      closePkgEdit();
      notify.success('Report package saved.');
    } catch (e) {
      notify.error(`Save failed: ${apiErr(e, 'Could not save package.')}`);
    } finally {
      setPkgSaving(false);
    }
  }, [pkgDetail, pkgRoleIds, notify, loadPackages, closePkgEdit]);

  const pkgEditSelected = useCallback(() => {
    if (pkgSel.size !== 1) { notify.warning('Select exactly one package to edit.'); return; }
    void openPkgEdit(String(Array.from(pkgSel)[0]));
  }, [pkgSel, notify, openPkgEdit]);
  const pkgDeleteSelected = useCallback(async () => {
    if (pkgSel.size === 0) { notify.warning('Select at least one package to delete.'); return; }
    const ids = Array.from(pkgSel).map(String);
    const names = pkgRows.filter(r => ids.includes(r.oid)).map(r => r.packageName).join(', ');
    if (!(await askConfirm(`Delete ${ids.length} package${ids.length === 1 ? '' : 's'}`, `Delete ${ids.length} package${ids.length === 1 ? '' : 's'}? ${names}`))) return;
    try {
      await Promise.all(ids.map(id => api.delete(`/api/v1/report-packages/${id}`)));
      notify.success(`Deleted ${ids.length} package${ids.length === 1 ? '' : 's'}.`);
      setPkgSel(new Set());
      await loadPackages();
    } catch (e) {
      notify.error(`Delete failed: ${apiErr(e, 'Could not delete.')}`);
      await loadPackages();
    }
  }, [pkgSel, pkgRows, notify, loadPackages, askConfirm]);

  // ── Report Package inline row editing (header fields) ────────────────────
  const [pkgInlineId, setPkgInlineId]       = useState<string | null>(null);
  const [pkgInlineDraft, setPkgInlineDraft] = useState<ReportPackageDetail | null>(null);
  const [pkgInlineRoleIds, setPkgInlineRoleIds] = useState<Set<string>>(new Set());
  const [pkgInlineSaving, setPkgInlineSaving]   = useState(false);

  const startPkgInline = useCallback(async (oid: string) => {
    if (!pkgLookupsLoaded) await loadPkgLookups();
    try {
      const res = await api.get(`/api/v1/report-packages/${oid}`);
      const d = (res.data as { data: ReportPackageDetail }).data;
      setPkgInlineDraft(d);
      setPkgInlineRoleIds(new Set((d.userAccess ?? '').split(';').map(s => s.trim()).filter(Boolean)));
      setPkgInlineId(oid);
    } catch (e) { notify.error(`Failed to load package: ${apiErr(e, 'API error.')}`); }
  }, [pkgLookupsLoaded, loadPkgLookups, notify]);
  const cancelPkgInline = useCallback(() => { setPkgInlineId(null); setPkgInlineDraft(null); }, []);
  const pkgInlineSet = useCallback((field: keyof ReportPackageDetail, val: string | boolean | null) => {
    setPkgInlineDraft(d => (d ? ({ ...d, [field]: val } as ReportPackageDetail) : d));
  }, []);
  const savePkgInline = useCallback(async () => {
    if (!pkgInlineDraft) return;
    if (!pkgInlineDraft.packageName?.trim()) { notify.warning('Package Name is required.'); return; }
    const body = {
      packageName: pkgInlineDraft.packageName, customCaption: pkgInlineDraft.customCaption,
      categoryOid: pkgInlineDraft.categoryOid, reportTypeOid: pkgInlineDraft.reportTypeOid, clientOid: pkgInlineDraft.clientOid,
      isActive: pkgInlineDraft.isActive, eSign: pkgInlineDraft.eSign, amended: pkgInlineDraft.amended, amendedText: pkgInlineDraft.amendedText,
      levelOneSigner: pkgInlineDraft.levelOneSigner, levelTwoSigner: pkgInlineDraft.levelTwoSigner, levelThreeSigner: pkgInlineDraft.levelThreeSigner,
      userAccess: Array.from(pkgInlineRoleIds).join('; '),
      reports: pkgInlineDraft.reports.map(r => ({ reportName: r.reportName, customCaption: r.customCaption, displayPage: r.displayPage, pageCount: r.pageCount, sort: r.sort })),
    };
    setPkgInlineSaving(true);
    try {
      await api.put(`/api/v1/report-packages/${pkgInlineDraft.oid}`, body);
      await loadPackages();
      setPkgInlineId(null); setPkgInlineDraft(null);
      notify.success('Saved successfully.');
    } catch (e) { notify.error(`Save failed: ${apiErr(e, 'Could not save package.')}`); }
    finally { setPkgInlineSaving(false); }
  }, [pkgInlineDraft, pkgInlineRoleIds, notify, loadPackages]);
  // New / Delete a Category or Report Type from within the package inline editor.
  const pkgAddLookupInline = useCallback(async (kind: 'category' | 'reportType') => {
    const label = kind === 'category' ? 'Category' : 'Report Type';
    const r = await askName(label, label, true);
    const name = r?.name.trim();
    if (!name) return;
    const path = kind === 'category' ? 'categories' : 'report-types';
    try {
      const res = await api.post(`/api/v1/custom-report-templates/${path}`, { name, description: r?.description });
      const oid = (res.data as { data: { oid: string } }).data.oid;
      await loadPkgLookups();
      pkgInlineSet(kind === 'category' ? 'categoryOid' : 'reportTypeOid', oid);
      notify.success(`${label} "${name}" added.`);
    } catch (e) { notify.error(`Could not add ${label}: ${apiErr(e, 'API error.')}`); }
  }, [askName, loadPkgLookups, pkgInlineSet, notify]);
  // ── Editor modal state ───────────────────────────────────────────────────
  const [editorOpen, setEditorOpen]   = useState(false);
  const [detail, setDetail]           = useState<TemplateDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [perms, setPerms]             = useState<FormPermissionRow[]>([]);
  // Form Report Permission sub-grid + dialog UI state.
  const [permSel, setPermSel]         = useState<Set<string>>(new Set());  // checked permission rows (multi-select + select-all)
  // Form Report Permissions sub-grid sort (click a column header to sort).
  const [permSort, setPermSort] = useState<{ col: 'userAccess' | 'formName'; dir: 'asc' | 'desc' }>({ col: 'formName', dir: 'asc' });
  const permSortBy = (col: 'userAccess' | 'formName') =>
    setPermSort(s => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  const permSortArrow = (col: 'userAccess' | 'formName') =>
    permSort.col === col
      ? <i className={`ti ti-caret-${permSort.dir === 'asc' ? 'up' : 'down'}-filled crb-sort-ind`} />
      : <i className="ti ti-caret-up-down crb-sort-ind dim" />;
  const [permFilters, setPermFilters] = useState<Record<string, ColumnFilterValue>>({});
  const permText = (p: FormPermissionRow, col: string) =>
    col === 'userAccess' ? (roleNames(p.userAccess) || '') : (p.formName || '');
  const sortedPerms = (): FormPermissionRow[] => {
    const { col, dir } = permSort; const mul = dir === 'asc' ? 1 : -1;
    const shown = perms.filter(p => Object.entries(permFilters).every(([k, sel]) => passesColumnFilter(sel, permText(p, k))));
    return [...shown].sort((a, b) => permText(a, col).localeCompare(permText(b, col)) * mul);
  };
  const [permOpen, setPermOpen]       = useState(false);
  const [permEditOid, setPermEditOid] = useState<string | null>(null);  // null = New
  const [permFormsOid, setPermFormsOid] = useState<string>('');
  const [permRoleIds, setPermRoleIds] = useState<Set<string>>(new Set());
  const [permSaving, setPermSaving]   = useState(false);

  const openEdit = useCallback(async (id: number) => {
    setEditorOpen(true);
    setDetail(null);
    setPerms([]);
    setPermOpen(false);
    setPermSel(new Set());
    setDetailLoading(true);
    try {
      const [detRes, permRes] = await Promise.all([
        api.get(`/api/v1/custom-report-templates/${id}`),
        api.get(`/api/v1/custom-report-templates/${id}/form-permissions`),
      ]);
      setDetail((detRes.data as { data: TemplateDetail }).data);
      setPerms((permRes.data as { data: FormPermissionRow[] }).data ?? []);
    } catch (e) {
      notify.error(`Failed to load template: ${apiErr(e, 'API error.')}`);
      setEditorOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }, [notify]);

  const closeEditor = useCallback(() => {
    setEditorOpen(false); setDetail(null); setPerms([]);
    setPermOpen(false); setPermSel(new Set()); setEditorMax(false);
  }, []);

  // A blank template for "Save and New" (templateId 0 = create on save).
  const blankTemplate = (): TemplateDetail => ({
    templateId: 0, templateName: '', userDefinedName: null, customCaption: '',
    reportCategory: null, reportType: null, categoryOid: null, reportTypeOid: null,
    moduleName: null, businessObjectName: null, dataSourceQuery: null, reportLayoutXml: null,
    tableSchema: null, normalColumn: null, rowColumn: null, uniqueColumn: null,
    categoryId: 0, reportTypeId: 0, verticalColumnCount: 0,
    active: true, needSignOff: false, finalReport: false, showReportId: false,
    waterMark: false, cocSettings: false, allowMultipleJobId: false, isAdhoc: false,
    sort: 0, comment: null, userAccess: null, lastUpdatedDate: null, lastUpdatedBy: null,
  });
  const openNew = useCallback(() => {
    setEditorOpen(true); setDetail(blankTemplate()); setPerms([]);
    setPermOpen(false); setPermSel(new Set()); setDetailLoading(false);  // openNew
  }, []);

  const editSelected = useCallback(() => {
    if (selected.size !== 1) { notify.warning('Select exactly one template to edit.'); return; }
    void openEdit(Number(Array.from(selected)[0]));
  }, [selected, notify, openEdit]);

  // ── Embedded Report Designer (Design / Preview / New) — ported from Studio ──
  // While `designer` is set the Report Designer tab shows the full-page
  // <ReportDesignerFrame> instead of the grid; Back returns here and reloads
  // the list (a designer save may have created / renamed a template).
  const [designer, setDesigner] = useState<{ id: number | null; name: string | null; mode: DesignerMode } | null>(null);
  const runDelete = useDeleteFlow();

  const selectedTemplate = useCallback((): TemplateSummary | null => {
    if (selected.size !== 1) return null;
    const id = Number(Array.from(selected)[0]);
    return list.find(t => t.templateId === id) ?? null;
  }, [selected, list]);

  const openDesigner = useCallback((id: number | null, name: string | null, mode: DesignerMode = 'design') => {
    setDesigner({ id, name, mode });
  }, []);
  const closeDesigner = useCallback(() => { setDesigner(null); void loadList(); }, [loadList]);
  // The designer's Prev/Next pill switches reports without leaving the designer: resolve the
  // name → templateId from the loaded list (same name-sorted set the pill walks) and swap the
  // open report, keeping the current Design/Preview mode so the host header + Preview stay correct.
  const navigateDesigner = useCallback((name: string) => {
    const t = list.find(x => x.templateName === name);
    setDesigner(d => (d ? { id: t?.templateId ?? null, name, mode: d.mode } : d));
  }, [list]);

  const designSelected = useCallback(() => {
    const t = selectedTemplate();
    if (!t) { notify.warning('Select exactly one template to design.'); return; }
    if (!t.templateName) { notify.warning('This template has no name — set one via Edit first.'); return; }
    openDesigner(t.templateId, t.templateName, 'design');
  }, [selectedTemplate, notify, openDesigner]);

  const previewSelected = useCallback(() => {
    const t = selectedTemplate();
    if (!t) { notify.warning('Select exactly one template to preview.'); return; }
    if (!t.templateName) { notify.warning('This template has no name — set one via Edit first.'); return; }
    openDesigner(t.templateId, t.templateName, 'preview');
  }, [selectedTemplate, notify, openDesigner]);

  // Delete = the audited soft-deactivate (Active = 0) — shared confirm → reason
  // gate → "Deleted." (standing rules 1 / 7 / 11).  Rows stay listed as Inactive.
  const deleteSelected = useCallback(() => {
    if (selected.size === 0) { notify.warning('Select at least one template to delete.'); return; }
    const ids = Array.from(selected).map(Number);
    const names = ids.map(id => list.find(t => t.templateId === id)?.templateName ?? `#${id}`);
    const shown = names.slice(0, 5).join(', ') + (names.length > 5 ? ` … and ${names.length - 5} more` : '');
    void runDelete({
      formName: 'Custom Reporting',
      message: `Deactivate ${ids.length === 1 ? 'template' : `${ids.length} templates`} ${shown}? `
             + `${ids.length === 1 ? 'It is' : 'They are'} marked Inactive (never physically deleted) `
             + 'so existing reports still resolve.',
      delete: async () => {
        for (const id of ids) await api.delete(`/api/v1/custom-report-templates/${id}`);
      },
      onDeleted: () => { setSelected(new Set()); void loadList(); },
    });
  }, [selected, list, runDelete, notify, loadList]);

  // "Design" is a form-specific toolbar button (left slot) → its key is declared
  // here, next to it (standing rule 14b); New/Edit/Delete/Preview/Refresh come
  // from <AppToolbar> automatically.
  useHotkeys(tab === 'designer' && !designer
    ? [{ combo: 'alt+l', label: 'Design layout', group: 'Toolbar', order: 0,
         disabled: selected.size !== 1, run: designSelected }]
    : null);

  const updateField = useCallback(<K extends keyof TemplateDetail>(key: K, value: TemplateDetail[K]) => {
    setDetail(d => (d ? { ...d, [key]: value } : d));
  }, []);

  // ── Category / Report Type lookup New + Delete (beside the dropdowns) ─────
  const addLookup = useCallback(async (kind: 'category' | 'reportType') => {
    const label = kind === 'category' ? 'Category' : 'Report Type';
    const r = await askName(label, label, true);
    const name = r?.name.trim();
    if (!name) return;
    const path = kind === 'category' ? 'categories' : 'report-types';
    try {
      const res = await api.post(`/api/v1/custom-report-templates/${path}`, { name, description: r?.description });
      const oid = (res.data as { data: { oid: string } }).data.oid;
      await loadLookups();
      updateField(kind === 'category' ? 'categoryOid' : 'reportTypeOid', oid);
      notify.success(`${label} "${name}" added.`);
    } catch (e) {
      notify.error(`Could not add ${label}: ${apiErr(e, 'API error.')}`);
    }
  }, [askName, loadLookups, updateField, notify]);

  // Forms lookup New — the Form Report Permission dialog's Forms field
  // (ALPACA's "+" New + eraser-clear lookup buttons next to the Forms dropdown).
  const addForm = useCallback(async () => {
    const name = (await askName('Forms', 'Form name'))?.name.trim();
    if (!name) return;
    try {
      const res = await api.post('/api/v1/custom-report-templates/forms', { name });
      const oid = (res.data as { data: { oid: string } }).data.oid;
      await loadLookups();
      setPermFormsOid(oid);
      notify.success(`Form "${name}" added.`);
    } catch (e) {
      notify.error(`Could not add Form: ${apiErr(e, 'API error.')}`);
    }
  }, [askName, loadLookups, notify]);

  // Body mirrors CustomReportTemplateInput — all fields preserved (only the
  // ones shown on the form are user-editable; the rest round-trip unchanged).
  const toBody = (d: TemplateDetail) => ({
    templateName: d.templateName ?? '', userDefinedName: d.userDefinedName ?? '',
    customCaption: d.customCaption ?? '', reportCategory: d.reportCategory, reportType: d.reportType,
    categoryOid: d.categoryOid, reportTypeOid: d.reportTypeOid,
    moduleName: d.moduleName, businessObjectName: d.businessObjectName,
    dataSourceQuery: d.dataSourceQuery, reportLayoutXml: d.reportLayoutXml,
    tableSchema: d.tableSchema, normalColumn: d.normalColumn, rowColumn: d.rowColumn, uniqueColumn: d.uniqueColumn,
    categoryId: d.categoryId, reportTypeId: d.reportTypeId, verticalColumnCount: d.verticalColumnCount,
    active: d.active, needSignOff: d.needSignOff, finalReport: d.finalReport, showReportId: d.showReportId,
    waterMark: d.waterMark, cocSettings: d.cocSettings, allowMultipleJobId: d.allowMultipleJobId,
    isAdhoc: d.isAdhoc, sort: d.sort, comment: d.comment, userAccess: d.userAccess,
  });

  // ── Inline row editing (the row pencil — ALPACA inline-edit parity) ───────
  const [inlineId, setInlineId]       = useState<number | null>(null);
  const [inlineDraft, setInlineDraft] = useState<TemplateDetail | null>(null);
  const [inlineSaving, setInlineSaving] = useState(false);

  const startInline = useCallback(async (id: number) => {
    try {
      const res = await api.get(`/api/v1/custom-report-templates/${id}`);
      setInlineDraft((res.data as { data: TemplateDetail }).data);
      setInlineId(id);
    } catch (e) {
      notify.error(`Failed to load row: ${apiErr(e, 'API error.')}`);
    }
  }, [notify]);
  const cancelInline = useCallback(() => { setInlineId(null); setInlineDraft(null); }, []);
  const inlineSet = useCallback((field: keyof TemplateDetail, val: string | number | boolean | null) => {
    setInlineDraft(d => (d ? ({ ...d, [field]: val } as TemplateDetail) : d));
  }, []);
  const saveInline = useCallback(async () => {
    if (!inlineDraft) return;
    if (!inlineDraft.templateName?.trim()) { notify.warning('Template Name is required.'); return; }
    setInlineSaving(true);
    try {
      await api.put(`/api/v1/custom-report-templates/${inlineDraft.templateId}`, toBody(inlineDraft));
      await loadList();
      setInlineId(null); setInlineDraft(null);
      notify.success('Saved successfully.');
    } catch (e) {
      notify.error(`Save failed: ${apiErr(e, 'Check the API logs.')}`);
    } finally {
      setInlineSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineDraft, notify, loadList]);

  // Inline Category / Report Type lookup New + Delete (operate on inlineDraft).
  const addLookupInline = useCallback(async (kind: 'category' | 'reportType') => {
    const label = kind === 'category' ? 'Category' : 'Report Type';
    const r = await askName(label, label, true);
    const name = r?.name.trim();
    if (!name) return;
    const path = kind === 'category' ? 'categories' : 'report-types';
    try {
      const res = await api.post(`/api/v1/custom-report-templates/${path}`, { name, description: r?.description });
      const oid = (res.data as { data: { oid: string } }).data.oid;
      await loadLookups();
      inlineSet(kind === 'category' ? 'categoryOid' : 'reportTypeOid', oid);
      notify.success(`${label} "${name}" added.`);
    } catch (e) { notify.error(`Could not add ${label}: ${apiErr(e, 'API error.')}`); }
  }, [askName, loadLookups, inlineSet, notify]);

  /** Save the template.  `andClose` closes after; `andNew` opens a fresh blank
   *  (ALPACA's Save and New).  A templateId of 0 means create (POST), else update. */
  const save = useCallback(async (andClose: boolean, andNew = false) => {
    if (!detail) return;
    if (!detail.templateName?.trim()) { notify.warning('Template Name is required.'); return; }
    const isNew = !detail.templateId;
    let savedId = detail.templateId;
    setSaving(true);
    try {
      await runSave({
        save: async () => {
          if (isNew) {
            const res = await api.post('/api/v1/custom-report-templates', toBody(detail));
            savedId = (res.data as { data: { templateId: number } }).data.templateId;
          } else {
            await api.put(`/api/v1/custom-report-templates/${detail.templateId}`, toBody(detail));
          }
        },
        successMessage: 'Saved successfully.',
        onSaved: async () => {
          await loadList();
          if (andNew) openNew();
          else if (andClose) closeEditor();
          else await openEdit(savedId);   // reload in place (Save, stay open)
        },
        onError: (e) => notify.error(`Save failed: ${apiErr(e, 'Check the API logs.')}`),
      });
    } finally {
      setSaving(false);
    }
  }, [detail, notify, runSave, loadList, closeEditor, openEdit, openNew]);

  // ── Form Report Permission sub-grid + dialog handlers ────────────────────
  const reloadPerms = useCallback(async (templateId: number) => {
    try {
      const res = await api.get(`/api/v1/custom-report-templates/${templateId}/form-permissions`);
      setPerms((res.data as { data: FormPermissionRow[] }).data ?? []);
      setPermSel(new Set());
    } catch { /* keep previous */ }
  }, []);

  // ── User Access picker ("Assign Users" — ALPACA's role picker, image 3) ──
  const [uaOpen, setUaOpen]     = useState(false);
  const [uaSearch, setUaSearch] = useState('');
  const [uaSel, setUaSel]       = useState<Set<string>>(new Set());
  // Target: the New/Edit dialog's selection, OR a specific saved permission row.
  const [uaTarget, setUaTarget] = useState<{ kind: 'dialog' } | { kind: 'row'; oid: string; formsOid: string | null } | null>(null);
  const [uaSaving, setUaSaving] = useState(false);

  const openUaDialog = useCallback(() => {
    setUaTarget({ kind: 'dialog' }); setUaSel(new Set(permRoleIds)); setUaSearch(''); setUaOpen(true);
  }, [permRoleIds]);
  const openUaRow = useCallback((row: FormPermissionRow) => {
    setUaTarget({ kind: 'row', oid: row.oid, formsOid: row.formsOid });
    setUaSel(new Set((row.userAccess ?? '').split(';').map(s => s.trim()).filter(Boolean)));
    setUaSearch(''); setUaOpen(true);
  }, []);
  const toggleUa = useCallback((oid: string) => {
    setUaSel(prev => { const n = new Set(prev); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  }, []);
  const uaConfirm = useCallback(async () => {
    if (!uaTarget) return;
    if (uaTarget.kind === 'dialog') { setPermRoleIds(new Set(uaSel)); setUaOpen(false); return; }
    if (!detail) return;
    setUaSaving(true);
    try {
      await api.put(`/api/v1/custom-report-templates/form-permissions/${uaTarget.oid}`,
        { formsOid: uaTarget.formsOid, userAccess: Array.from(uaSel).join('; ') });
      await reloadPerms(detail.templateId);
      setUaOpen(false);
      notify.success('User access updated.');
    } catch (e) { notify.error(`Save failed: ${apiErr(e, 'Could not save.')}`); }
    finally { setUaSaving(false); }
  }, [uaTarget, uaSel, detail, reloadPerms, notify]);

  const openPermNew = useCallback(() => {
    setPermEditOid(null); setPermFormsOid(''); setPermRoleIds(new Set()); setPermOpen(true);
  }, []);
  const openPermEdit = useCallback((row: FormPermissionRow) => {
    setPermEditOid(row.oid);
    setPermFormsOid(row.formsOid ?? '');
    setPermRoleIds(new Set((row.userAccess ?? '').split(';').map(s => s.trim()).filter(Boolean)));
    setPermOpen(true);
  }, []);

  const savePerm = useCallback(async () => {
    if (!detail) return;
    if (!permFormsOid) { notify.warning('Forms is required.'); return; }
    const userAccess = Array.from(permRoleIds).join('; ');
    setPermSaving(true);
    try {
      if (permEditOid) {
        await api.put(`/api/v1/custom-report-templates/form-permissions/${permEditOid}`, { formsOid: permFormsOid, userAccess });
      } else {
        await api.post(`/api/v1/custom-report-templates/${detail.templateId}/form-permissions`, { formsOid: permFormsOid, userAccess });
      }
      await reloadPerms(detail.templateId);
      setPermOpen(false);
      void showAlert({ message: 'Form report permission saved.', tone: 'success' });   // centered popup (ALPACA)
    } catch (e) {
      notify.error(`Save failed: ${apiErr(e, 'Could not save permission.')}`);
    } finally {
      setPermSaving(false);
    }
  }, [detail, permFormsOid, permRoleIds, permEditOid, notify, reloadPerms, showAlert]);

  // Delete ALL checked permission rows after ONE centered confirm (select-all → Delete).
  const deletePerms = useCallback(async (rows: FormPermissionRow[]) => {
    if (!detail || rows.length === 0) return;
    const ok = await showConfirm({
      title: 'Delete', tone: 'danger',
      message: `Delete ${rows.length} form report permission${rows.length === 1 ? '' : 's'}?`,
    });
    if (!ok) return;
    try {
      await Promise.all(rows.map(r => api.delete(`/api/v1/custom-report-templates/form-permissions/${r.oid}`)));
      await reloadPerms(detail.templateId);
      void showAlert({ message: `Deleted ${rows.length} permission${rows.length === 1 ? '' : 's'}.`, tone: 'success' });
    } catch (e) {
      notify.error(`Delete failed: ${apiErr(e, 'Could not delete.')}`);
    }
  }, [detail, notify, reloadPerms, showConfirm, showAlert]);

  // ── Columns ──────────────────────────────────────────────────────────────
  // Narrow widths so every column fits without horizontal scroll (ALPACA parity).
  // Each editable cell shows an inline input when ITS row is being inline-edited.
  const boolCol = (field: keyof TemplateSummary, header: string, width: number): AppColumn<TemplateSummary> => ({
    field: field as string, header, align: 'center', width, filterable: false,
    text: r => (r[field] ? 'Yes' : 'No'),
    render: r => {
      const editing = inlineId === r.templateId && inlineDraft;
      const checked = editing ? Boolean(inlineDraft![field as keyof TemplateDetail]) : Boolean(r[field]);
      return editing
        ? <input type="checkbox" className="crb-cbx" checked={checked}
                 onClick={e => e.stopPropagation()}
                 onChange={e => inlineSet(field as keyof TemplateDetail, e.target.checked)} />
        : <span className={`cellcheck${checked ? ' on' : ''}`} />;   /* shared read-only boolean cell (BO UI Standard) */
    },
  });
  const inlineText = (r: TemplateSummary, field: keyof TemplateDetail, display: string | null) =>
    inlineId === r.templateId
      ? <input className="crb-inline-inp" value={String(inlineDraft?.[field] ?? '')}
               onClick={e => e.stopPropagation()} onChange={e => inlineSet(field, e.target.value)} />
      : <span>{display}</span>;

  // Inline Category / Report Type editor: dropdown + New/Delete buttons (ALPACA).
  const inlineLookup = (kind: 'category' | 'reportType') => {
    const oid = kind === 'category' ? inlineDraft?.categoryOid : inlineDraft?.reportTypeOid;
    const opts = kind === 'category' ? categories : reportTypes;
    const key = kind === 'category' ? 'categoryOid' : 'reportTypeOid';
    return (
      <span onClick={e => e.stopPropagation()} style={{ display: 'block' }}>
        <AiCombo compact value={oid ?? ''}
                 options={opts.map(o => ({ value: o.oid, label: o.name ?? '(unnamed)' }))}
                 onChange={v => inlineSet(key, v)}
                 onNew={() => void addLookupInline(kind)}
                 newTitle={`New ${kind === 'category' ? 'category' : 'report type'}`} />
      </span>
    );
  };

  const designerCols: AppColumn<TemplateSummary>[] = [
    // Icon 1 — inline row edit (pencil → inline; Save / Cancel while editing).
    { field: '_edit', header: '', width: 52, align: 'center', sortable: false, filterable: false,
      render: r => (inlineId === r.templateId ? (
        <span className="crb-inline-actions">
          <button className="crb-row-edit save" title="Save" disabled={inlineSaving}
                  onClick={e => { e.stopPropagation(); void saveInline(); }}>
            <BusyIcon busy={inlineSaving} icon="ti-device-floppy" />
          </button>
          <button className="crb-row-edit cancel" title="Cancel"
                  onClick={e => { e.stopPropagation(); cancelInline(); }}>
            <i className="ti ti-arrow-back-up" />
          </button>
        </span>
      ) : (
        <button className="crb-row-edit" title="Inline edit (edit in the row)"
                onClick={e => { e.stopPropagation(); void startInline(r.templateId); }}>
          <i className="ti ti-edit" />
        </button>
      )) },
    // Icon 2 — open the full edit-screen modal.
    { field: '_editscreen', header: '', width: 40, align: 'center', sortable: false, filterable: false,
      render: r => (
        <button className="crb-row-edit screen" title="Open full edit screen"
                onClick={e => { e.stopPropagation(); void openEdit(r.templateId); }}>
          <i className="ti ti-external-link" />
        </button>
      ) },
    // Icon 3 — open the report LAYOUT in the embedded designer (Studio's row Edit).
    { field: '_design', header: '', width: 40, align: 'center', sortable: false, filterable: false,
      render: r => (
        <button className="crb-row-edit design" title="Design the report layout"
                disabled={!r.templateName}
                onClick={e => { e.stopPropagation(); if (r.templateName) openDesigner(r.templateId, r.templateName, 'design'); }}>
          <i className="ti ti-layout-grid" />
        </button>
      ) },
    { field: 'templateId', header: 'TemplateID', type: 'number', align: 'left', width: 72 },
    { field: 'templateName', header: 'TemplateName', width: 150,
      render: r => inlineText(r, 'templateName', r.templateName) },
    { field: 'customCaption', header: 'CustomCaption', width: 140,
      render: r => inlineText(r, 'customCaption', r.customCaption) },
    { field: 'reportCategory', header: 'Category', width: 146,
      render: r => inlineId === r.templateId ? inlineLookup('category') : <span>{r.reportCategory}</span> },
    { field: 'reportType', header: 'ReportType', width: 138,
      render: r => inlineId === r.templateId ? inlineLookup('reportType') : <span>{r.reportType}</span> },
    boolCol('showReportId', 'ShowReportID', 96),
    boolCol('active', 'Active', 64),
    boolCol('finalReport', 'FinalReport', 90),
    boolCol('waterMark', 'WaterMark', 88),
    boolCol('allowMultipleJobId', 'AllowMultipleJOBID', 138),
    { field: 'comment', header: 'Comment', width: 110,
      render: r => inlineText(r, 'comment', r.comment) },
    { field: 'sort', header: 'Sort', type: 'number', width: 54,
      render: r => inlineId === r.templateId
        ? <input type="number" className="crb-inline-inp" value={inlineDraft?.sort ?? 0}
                 onClick={e => e.stopPropagation()} onChange={e => inlineSet('sort', Number(e.target.value) || 0)} />
        : <span>{r.sort}</span> },
    boolCol('isAdhoc', 'IsAdHocReport', 108),
  ];

  // Inline Category / Report Type editor for a package row.
  const pkgInlineLookup = (kind: 'category' | 'reportType') => {
    const oid = kind === 'category' ? pkgInlineDraft?.categoryOid : pkgInlineDraft?.reportTypeOid;
    const opts = kind === 'category' ? pkgCategories : pkgReportTypes;
    const key = kind === 'category' ? 'categoryOid' : 'reportTypeOid';
    return (
      <span onClick={e => e.stopPropagation()} style={{ display: 'block' }}>
        <AiCombo compact value={oid ?? ''}
                 options={opts.map(o => ({ value: o.oid, label: o.name ?? '(unnamed)' }))}
                 onChange={v => pkgInlineSet(key, v)}
                 onNew={() => void pkgAddLookupInline(kind)}
                 newTitle={`New ${kind === 'category' ? 'category' : 'report type'}`} />
      </span>
    );
  };
  const pkgEditing = (r: ReportPackageListRow) => pkgInlineId === r.oid && pkgInlineDraft;

  // Report Package grid columns — one row per package (ALPACA Report Package list).
  const packageCols: AppColumn<ReportPackageListRow>[] = [
    // Icon 1 — inline edit (header fields); Save / Cancel while editing.
    { field: '_edit', header: '', width: 52, align: 'center', sortable: false, filterable: false,
      render: r => (pkgInlineId === r.oid ? (
        <span className="crb-inline-actions">
          <button className="crb-row-edit save" title="Save" disabled={pkgInlineSaving}
                  onClick={e => { e.stopPropagation(); void savePkgInline(); }}>
            <BusyIcon busy={pkgInlineSaving} icon="ti-device-floppy" />
          </button>
          <button className="crb-row-edit cancel" title="Cancel"
                  onClick={e => { e.stopPropagation(); cancelPkgInline(); }}><i className="ti ti-arrow-back-up" /></button>
        </span>
      ) : (
        <button className="crb-row-edit" title="Inline edit (edit in the row)"
                onClick={e => { e.stopPropagation(); void startPkgInline(r.oid); }}><i className="ti ti-edit" /></button>
      )) },
    // Icon 2 — open the full package edit screen.
    { field: '_editscreen', header: '', width: 40, align: 'center', sortable: false, filterable: false,
      render: r => (
        <button className="crb-row-edit screen" title="Open full edit screen"
                onClick={e => { e.stopPropagation(); void openPkgEdit(r.oid); }}><i className="ti ti-external-link" /></button>
      ) },
    { field: 'packageName', header: 'PackageName', width: 260,
      render: r => pkgEditing(r)
        ? <input className="crb-inline-inp" value={pkgInlineDraft?.packageName ?? ''} onClick={e => e.stopPropagation()}
                 onChange={e => pkgInlineSet('packageName', e.target.value)} />
        : <span>{r.packageName}</span> },
    { field: 'customCaption', header: 'CustomCaption', width: 170,
      render: r => pkgEditing(r)
        ? <input className="crb-inline-inp" value={pkgInlineDraft?.customCaption ?? ''} onClick={e => e.stopPropagation()}
                 onChange={e => pkgInlineSet('customCaption', e.target.value)} />
        : <span>{r.customCaption}</span> },
    { field: 'category', header: 'Category', width: 150,
      render: r => pkgEditing(r) ? pkgInlineLookup('category') : <span>{r.category}</span> },
    { field: 'reportType', header: 'ReportType', width: 140,
      render: r => pkgEditing(r) ? pkgInlineLookup('reportType') : <span>{r.reportType}</span> },
    { field: 'userAccess', header: 'UserAccess', width: 240, text: r => pkgRoleNames(r.userAccess),
      render: r => pkgEditing(r)
        ? <RoleMultiSelect roles={pkgRoles} selected={pkgInlineRoleIds} onChange={setPkgInlineRoleIds} />
        : <span>{pkgRoleNames(r.userAccess)}</span> },
    { field: 'amended', header: 'Amended', align: 'center', width: 100,
      text: r => (r.amended ? 'Yes' : 'No'),
      render: r => pkgEditing(r)
        ? <input type="checkbox" className="crb-cbx" checked={pkgInlineDraft?.amended ?? false}
                 onClick={e => e.stopPropagation()} onChange={e => pkgInlineSet('amended', e.target.checked)} />
        : <span className={`cellcheck${r.amended ? ' on' : ''}`} /> },   /* shared read-only boolean cell */
  ];

  // ── CSV export ───────────────────────────────────────────────────────────
  // ── Export to (CSV / Excel) — ALPACA-style multi-format dropdown ──────────
  type Cell = string | number | boolean | null;
  function designerData(): { headers: string[]; rows: Cell[][] } {
    return {
      headers: ['TemplateID', 'TemplateName', 'CustomCaption', 'Category', 'ReportType',
        'ShowReportID', 'Active', 'FinalReport', 'WaterMark', 'AllowMultipleJOBID', 'Comment', 'Sort', 'IsAdHocReport'],
      rows: list.map(r => [r.templateId, r.templateName, r.customCaption, r.reportCategory, r.reportType,
        r.showReportId, r.active, r.finalReport, r.waterMark, r.allowMultipleJobId, r.comment, r.sort, r.isAdhoc]),
    };
  }
  function packageData(): { headers: string[]; rows: Cell[][] } {
    return {
      headers: ['PackageName', 'CustomCaption', 'Category', 'ReportType', 'UserAccess', 'Amended', 'Active'],
      rows: pkgRows.map(r => [r.packageName, r.customCaption, r.category, r.reportType, pkgRoleNames(r.userAccess), r.amended, r.isActive]),
    };
  }
  type GridData = { headers: string[]; rows: Cell[][] };
  function q(v: Cell): string { return `"${String(v ?? '').replace(/"/g, '""')}"`; }
  function htmlEsc(v: Cell): string { return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  function htmlTable(d: GridData): string {
    const head = `<tr>${d.headers.map(h => `<th>${htmlEsc(h)}</th>`).join('')}</tr>`;
    const body = d.rows.map(r => `<tr>${r.map(c => `<td>${htmlEsc(c)}</td>`).join('')}</tr>`).join('');
    return `<table border="1" style="border-collapse:collapse;font:13px sans-serif"><thead>${head}</thead><tbody>${body}</tbody></table>`;
  }
  function exportCsv(d: GridData, name: string) {
    const csv = [d.headers.map(q).join(','), ...d.rows.map(r => r.map(q).join(','))].join('\n');
    downloadBlob(new Blob([csv], { type: 'text/csv' }), `${name}.csv`);
  }
  function exportText(d: GridData, name: string) {
    const txt = [d.headers, ...d.rows].map(r => r.map(c => String(c ?? '')).join('\t')).join('\r\n');
    downloadBlob(new Blob([txt], { type: 'text/plain' }), `${name}.txt`);
  }
  function exportHtml(d: GridData, name: string) {
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${htmlEsc(name)}</title></head><body>${htmlTable(d)}</body></html>`;
    downloadBlob(new Blob([html], { type: 'text/html' }), `${name}.html`);
  }
  function exportMht(d: GridData, name: string) {
    const html = `<html><head><meta charset="utf-8"></head><body>${htmlTable(d)}</body></html>`;
    const mht = `From: <Saved by Alpaca Intelligence>\r\nSubject: ${name}\r\nMIME-Version: 1.0\r\nContent-Type: text/html; charset="utf-8"\r\n\r\n${html}`;
    downloadBlob(new Blob([mht], { type: 'message/rfc822' }), `${name}.mht`);
  }
  function exportRtf(d: GridData, name: string) {
    const esc = (v: Cell) => String(v ?? '').replace(/\\/g, '\\\\').replace(/[{}]/g, m => '\\' + m);
    const lines = [d.headers, ...d.rows].map(r => r.map(esc).join('\\tab ')).join('\\par\r\n');
    const rtf = `{\\rtf1\\ansi\\deff0 ${lines}\\par}`;
    downloadBlob(new Blob([rtf], { type: 'application/rtf' }), `${name}.rtf`);
  }
  function exportXls(d: GridData, name: string) {
    const ws = XLSX.utils.aoa_to_sheet([d.headers, ...d.rows.map(r => r.map(c => c ?? ''))]);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${name}.xls`, { bookType: 'biff8' });
  }
  function exportXlsx(d: GridData, name: string) {
    const ws = XLSX.utils.aoa_to_sheet([d.headers, ...d.rows.map(r => r.map(c => c ?? ''))]);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${name}.xlsx`);
  }
  function exportPdf(d: GridData, name: string) {
    const doc = new jsPDF({ orientation: 'landscape' });
    autoTable(doc, {
      head: [d.headers], body: d.rows.map(r => r.map(c => String(c ?? ''))),
      styles: { fontSize: 8 }, headStyles: { fillColor: [30, 58, 95] }, margin: { top: 14 },
    });
    doc.save(`${name}.pdf`);
  }
  function downloadBlob(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }
  /** The ALPACA "Export to" format set (same order as LIMS).  Image & DOCX are
   *  omitted — they need extra libraries (html2canvas / docx). */
  function exportMenu(getData: () => GridData, name: string): ExportOption[] {
    return [
      { label: 'CSV File',  icon: 'ti-file-type-csv',  onSelect: () => exportCsv(getData(), name) },
      { label: 'HTML File', icon: 'ti-file-type-html', onSelect: () => exportHtml(getData(), name) },
      { label: 'MHT File',  icon: 'ti-world-code',      onSelect: () => exportMht(getData(), name) },
      { label: 'Text File', icon: 'ti-file-text',       onSelect: () => exportText(getData(), name) },
      { label: 'PDF File',  icon: 'ti-file-type-pdf',   onSelect: () => exportPdf(getData(), name) },
      { label: 'RTF File',  icon: 'ti-file-type-doc',   onSelect: () => exportRtf(getData(), name) },
      { label: 'XLS File',  icon: 'ti-file-type-xls',   onSelect: () => exportXls(getData(), name) },
      { label: 'XLSX File', icon: 'ti-file-type-xls',   onSelect: () => exportXlsx(getData(), name) },
    ];
  }
  function reportsData(): GridData {
    return {
      headers: ['ReportName', 'CustomCaption', 'DisplayPage', 'IncludePageCount', 'Sort'],
      rows: (pkgDetail?.reports ?? []).map(r => [r.reportName, r.customCaption, r.displayPage, r.pageCount, r.sort]),
    };
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{crbStyles}</style>

      <div className="bl-page">
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Settings</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Custom Report Builder</span>
            </div>
            {/* Heading is the ONE shared <FormTitle> in the section header below
                (BO UI Standard §7) — no duplicate hand-rolled h1 here. */}
            <div className="dp-subtitle">
              Report templates and packages
            </div>
          </div>
        </div>

        <div className="bl-grid-area">
          <div className="bl-card bl-card-flex">

            {/* Tabs — Report Designer / Report Package.  The Report Package tab is
                hidden while the full-page Design/Preview view is open (`designer`
                set), so the layout surface owns the whole form. */}
            <div className="crb-tabs">
              <button className={`crb-tab${tab === 'designer' ? ' active' : ''}`} onClick={() => setTab('designer')}>
                <i className="ti ti-layout-grid" /> Report Designer
              </button>
              {!designer && (
                <button className={`crb-tab${tab === 'package' ? ' active' : ''}`} onClick={() => setTab('package')}>
                  <i className="ti ti-package" /> Report Package
                </button>
              )}
            </div>

            {tab === 'designer' && designer ? (
              /* Full-page Design / Preview view — the list is REPLACED, not popped up. */
              <ReportDesignerFrame
                reportName={designer.name}
                templateId={designer.id}
                mode={designer.mode}
                onModeChange={mode => setDesigner(d => (d ? { ...d, mode } : d))}
                onBack={closeDesigner}
                onNavigate={navigateDesigner}
              />
            ) : tab === 'designer' ? (
              <>
                <div className="section-hdr" style={{ padding: '8px 14px', borderBottom: '1px solid var(--ai-line)', margin: 0, background: 'var(--ai-header-bg)', justifyContent: 'flex-start', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {/* ONE shared <FormTitle> — nav icon/colour + TOP load-time chip +
                        the quiet audit chip (backend audits under "Custom Reporting"). */}
                    <FormTitle pageId="custreportbuilder" title="Report Designer" loadMs={listMs} loading={listLoading}
                               auditFormName="Custom Reporting" />
                    {selected.size > 0 && (
                      <span className="crb-sel-pill" style={{ marginLeft: 10 }}>
                        <i className="ti ti-circle-check" /> {selected.size} selected
                        <button className="crb-sel-clear" onClick={() => setSelected(new Set())} title="Clear selection">×</button>
                      </span>
                    )}
                  </div>
                  {/* Toolbar — Design (form-specific) · New · Edit · Delete · Preview ·
                      Refresh · Export to.  New opens a BLANK designer (Studio parity —
                      the designer's Save creates the row); Edit = the settings editor;
                      Design / Preview open the selected template's layout. */}
                  <AppToolbar
                    left={
                      <button type="button" className="ca-btn" onClick={designSelected}
                              disabled={selected.size !== 1}
                              title={`Design the selected template's layout (${formatCombo('alt+l')})`}>
                        <i className="ti ti-layout-grid" /> Design
                      </button>
                    }
                    onNew={() => openDesigner(null, null, 'design')}
                    onEdit={editSelected}
                    editDisabled={selected.size !== 1}
                    onDelete={deleteSelected}
                    deleteDisabled={selected.size === 0}
                    onPreview={previewSelected}
                    previewDisabled={selected.size !== 1}
                    onRefresh={loadList}
                    refreshing={listLoading}
                    exportOptions={exportMenu(designerData, 'custom-report-templates')}
                  />
                </div>

                <AppDataGrid<TemplateSummary>
                  className="crb-designer-grid"
                  rows={list}
                  columns={designerCols}
                  getRowId={r => r.templateId}
                  loading={listLoading}
                  selectable
                  selectedIds={selected}
                  onSelectionChange={setSelected}
                  emptyText="No report templates found"
                />
              </>
            ) : (
              <>
                <div className="section-hdr" style={{ padding: '8px 14px', borderBottom: '1px solid var(--ai-line)', margin: 0, background: 'var(--ai-header-bg)', justifyContent: 'flex-start', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FormTitle pageId="custreportbuilder" title="Report Package" loadMs={pkgMs} loading={pkgLoading}
                               auditFormName="Custom Reporting" />
                    {pkgSel.size > 0 && (
                      <span className="crb-sel-pill" style={{ marginLeft: 10 }}>
                        <i className="ti ti-circle-check" /> {pkgSel.size} selected
                        <button className="crb-sel-clear" onClick={() => setPkgSel(new Set())} title="Clear selection">×</button>
                      </span>
                    )}
                  </div>
                  <AppToolbar
                    onNew={() => void openPkgNew()}
                    newLabel="New Package"
                    onEdit={pkgEditSelected}
                    editDisabled={pkgSel.size !== 1}
                    onDelete={pkgDeleteSelected}
                    deleteDisabled={pkgSel.size === 0}
                    onRefresh={loadPackages}
                    refreshing={pkgLoading}
                    exportOptions={exportMenu(packageData, 'report-packages')}
                  />
                </div>

                <AppDataGrid<ReportPackageListRow>
                  rows={pkgRows}
                  columns={packageCols}
                  getRowId={r => r.oid}
                  loading={pkgLoading}
                  selectable
                  selectedIds={pkgSel}
                  onSelectionChange={setPkgSel}
                  emptyText="No report packages found"
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Settings editor modal — mirrors ALPACA CustomReportBuilder_DetailView ── */}
      {editorOpen && (
        <div className="crb-overlay" onClick={e => { if (e.target === e.currentTarget) closeEditor(); }}>
          <div className={`crb-modal${editorMax ? ' crb-modal--max' : ''}`}>
            <div className="crb-modal-hdr">
              <div className="crb-modal-hdr-l">
                <i className="ti ti-edit" style={{ fontSize: 16 }} />
                <span className="crb-modal-hdr-title">
                  Custom Report Builder{detail?.templateName ? ` - ${detail.templateName}` : ''}
                </span>
              </div>
              <div className="crb-modal-hdr-btns">
                <button className="crb-modal-close crb-modal-max" title={editorMax ? 'Restore' : 'Maximize'} onClick={() => setEditorMax(m => !m)}>
                  <i className={`ti ${editorMax ? 'ti-minimize' : 'ti-maximize'}`} />
                </button>
                <button className="crb-modal-close" onClick={closeEditor}>×</button>
              </div>
            </div>

            <div className="crb-modal-body">
              {detailLoading || !detail ? (
                <div style={{ padding: 24, color: 'var(--ai-muted)' }}>Loading…</div>
              ) : (
                <>
                  {/* Two columns: left = fields, right = flag checkboxes (ALPACA layout) */}
                  <div className="crb-two-col">
                    <div className="crb-fields">
                      <Row label="TemplateID:">
                        <input value={detail.templateId || ''} readOnly className="crb-ro" />
                      </Row>
                      <Row label="TemplateName:">
                        <input value={detail.templateName ?? ''} autoFocus
                               onChange={e => updateField('templateName', e.target.value)} />
                      </Row>
                      <Row label="Custom Caption:">
                        <input value={detail.customCaption ?? ''}
                               onChange={e => updateField('customCaption', e.target.value)} />
                      </Row>
                      <Row label="Category:">
                        <AiCombo value={detail.categoryOid ?? ''}
                                 options={categories.map(c => ({ value: c.oid, label: c.name ?? '(unnamed)' }))}
                                 onChange={v => updateField('categoryOid', v)}
                                 onNew={() => void addLookup('category')} newTitle="New category" />
                      </Row>
                      <Row label="Report Type:">
                        <AiCombo value={detail.reportTypeOid ?? ''}
                                 options={reportTypes.map(t => ({ value: t.oid, label: t.name ?? '(unnamed)' }))}
                                 onChange={v => updateField('reportTypeOid', v)}
                                 onNew={() => void addLookup('reportType')} newTitle="New report type" />
                      </Row>
                      <Row label="Comment:">
                        <textarea value={detail.comment ?? ''} rows={3}
                                  onChange={e => updateField('comment', e.target.value)} />
                      </Row>
                    </div>

                    <div className="crb-flags">
                      <Flag label="Show Report ID"      on={detail.showReportId}       set={v => updateField('showReportId', v)} />
                      <Flag label="Active"              on={detail.active}             set={v => updateField('active', v)} />
                      <Flag label="Water Mark"          on={detail.waterMark}          set={v => updateField('waterMark', v)} />
                      <Flag label="Final Report"        on={detail.finalReport}        set={v => updateField('finalReport', v)} />
                      <Flag label="Allow Multiple JOBID" on={detail.allowMultipleJobId} set={v => updateField('allowMultipleJobId', v)} />
                    </div>
                  </div>

                  {/* Form Report Permissions sub-grid — New / Edit / Delete via dialog */}
                  <div className="crb-perm-section">
                    <div className="crb-perm-title">Form Report Permissions</div>
                    {/* Shared .ca-btn action bar — ONE solid primary (New), prototype glyphs. */}
                    <div className="crb-perm-toolbar">
                      <button className="ca-btn ca-btn-solid" onClick={openPermNew}><span className="tb-i">＋</span> New</button>
                      <button className="ca-btn" disabled={permSel.size !== 1}
                              onClick={() => { const r = perms.find(p => permSel.has(p.oid)); if (r) openPermEdit(r); }}>
                        <span className="tb-i tb-i-blue">✎</span> Edit
                      </button>
                      <button className="ca-btn" disabled={permSel.size === 0}
                              onClick={() => void deletePerms(perms.filter(p => permSel.has(p.oid)))}>
                        <span className="tb-i tb-i-red">🗑</span> Delete
                      </button>
                    </div>
                    <table className="crb-perm-tbl">
                      <thead><tr>
                        <th style={{ width: 30, textAlign: 'center' }}>
                          <input type="checkbox"
                                 checked={perms.length > 0 && permSel.size === perms.length}
                                 ref={el => { if (el) el.indeterminate = permSel.size > 0 && permSel.size < perms.length; }}
                                 onChange={e => setPermSel(e.target.checked ? new Set(perms.map(p => p.oid)) : new Set())} />
                        </th>
                        <th style={{ width: 32 }} />
                        <th className="crb-sortable" onClick={() => permSortBy('userAccess')}>
                          UsersAccess{permSortArrow('userAccess')}
                          <ColFilterButton variant="inline" col="userAccess" label="UsersAccess" rows={perms}
                            formatter={p => permText(p, 'userAccess')} value={permFilters.userAccess ?? null}
                            onChange={v => setPermFilters(f => ({ ...f, userAccess: v }))} />
                        </th>
                        <th className="crb-sortable" onClick={() => permSortBy('formName')}>
                          Forms{permSortArrow('formName')}
                          <ColFilterButton variant="inline" col="formName" label="Forms" rows={perms}
                            formatter={p => permText(p, 'formName')} value={permFilters.formName ?? null}
                            onChange={v => setPermFilters(f => ({ ...f, formName: v }))} />
                        </th>
                      </tr></thead>
                      <tbody>
                        {perms.length === 0 ? (
                          <tr><td colSpan={4} className="crb-perm-empty">No data to display</td></tr>
                        ) : sortedPerms().map(p => (
                          <tr key={p.oid} className={permSel.has(p.oid) ? 'sel' : ''}>
                            <td style={{ textAlign: 'center' }}>
                              <input type="checkbox" checked={permSel.has(p.oid)}
                                     onChange={() => setPermSel(prev => {
                                       const n = new Set(prev);
                                       if (n.has(p.oid)) n.delete(p.oid); else n.add(p.oid);
                                       return n;
                                     })} />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button className="crb-row-edit" title="Edit"
                                      onClick={e => { e.stopPropagation(); openPermEdit(p); }}>
                                <i className="ti ti-edit" />
                              </button>
                            </td>
                            <td><a className="crb-assign-link" title={roleNames(p.userAccess) || 'No roles'}
                                   onClick={e => { e.stopPropagation(); openUaRow(p); }}>Assign Users</a></td>
                            <td>{p.formName || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                </>
              )}
            </div>

            {/* Shared .ca-btn action bar — ONE solid primary (Save), prototype glyphs. */}
            <div className="crb-modal-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => save(false)}
                      disabled={detailLoading || saving || !detail?.templateName?.trim()}>
                {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
              </button>
              <button className="ca-btn" onClick={() => save(true)}
                      disabled={detailLoading || saving || !detail?.templateName?.trim()}>
                <span className="tb-i tb-i-green">💾</span> Save and Close
              </button>
              <button className="ca-btn" onClick={() => save(false, true)}
                      disabled={detailLoading || saving || !detail?.templateName?.trim()}>
                <span className="tb-i tb-i-green">🗎</span> Save and New
              </button>
              <button className="ca-btn" onClick={closeEditor}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Form Report Permission dialog (New / Edit) — ALPACA parity ── */}
      {editorOpen && permOpen && (
        <div className="crb-overlay2" onClick={e => { if (e.target === e.currentTarget) setPermOpen(false); }}>
          <div className="crb-perm-dialog">
            <div className="crb-perm-dlg-hdr">
              <span>Form Report Permission</span>
              <button className="crb-modal-close" onClick={() => setPermOpen(false)}>×</button>
            </div>
            <div className="crb-perm-dlg-body">
              <div className="crb-row">
                <label className="crb-row-lbl">Forms: <span className="crb-req">*</span></label>
                <div className="crb-row-ctl">
                  <AiCombo value={permFormsOid}
                           options={forms.map(f => ({ value: f.oid, label: f.name ?? '(unnamed)' }))}
                           onChange={v => setPermFormsOid(v ?? '')}
                           onNew={() => void addForm()} newTitle="New form" />
                </div>
              </div>
              <div className="crb-row" style={{ alignItems: 'start' }}>
                <label className="crb-row-lbl" style={{ paddingTop: 0 }}>User Access:</label>
                <div className="crb-row-ctl">
                  <button type="button" className="crb-ms-field" onClick={openUaDialog}>
                    <span className={permRoleIds.size ? '' : 'crb-muted'}>
                      {permRoleIds.size ? roleNames(Array.from(permRoleIds).join('; ')) : 'Assign Users…'}
                    </span>
                    <i className="ti ti-users" />
                  </button>
                </div>
              </div>
            </div>
            <div className="crb-perm-dlg-ftr">
              <button className="ca-btn ca-btn-solid" onClick={savePerm} disabled={permSaving || !permFormsOid}>
                {permSaving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} OK
              </button>
              <button className="ca-btn" onClick={() => setPermOpen(false)}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── User Access picker (Assign Users) — ALPACA role picker (image 3) ── */}
      {editorOpen && uaOpen && (
        <div className="crb-overlay2" onClick={e => { if (e.target === e.currentTarget) setUaOpen(false); }}>
          <div className="crb-pick-dialog">
            <div className="crb-perm-dlg-hdr">
              <span>User Access</span>
              <button className="crb-modal-close" onClick={() => setUaOpen(false)}>×</button>
            </div>
            <div className="crb-pick-search">
              {/* shared .list-search (boStandard.css) — never a bespoke search box */}
              <div className="list-search">
                <span className="tb-i">🔍</span>
                <input placeholder="Search…" value={uaSearch} autoFocus onChange={e => setUaSearch(e.target.value)} />
              </div>
            </div>
            <div className="crb-pick-list">
              {(() => {
                const term = uaSearch.trim().toLowerCase();
                const list = term ? roles.filter(r => (r.name ?? '').toLowerCase().includes(term)) : roles;
                if (list.length === 0) return <div className="crb-muted" style={{ padding: 12 }}>No roles found</div>;
                const allChecked = list.length > 0 && list.every(r => uaSel.has(r.oid));
                return (
                  <>
                    <label className="crb-role-item crb-role-all">
                      <input type="checkbox" checked={allChecked}
                             ref={el => { if (el) el.indeterminate = uaSel.size > 0 && !allChecked; }}
                             onChange={() => setUaSel(prev => {
                               const n = new Set(prev);
                               if (allChecked) list.forEach(r => n.delete(r.oid)); else list.forEach(r => n.add(r.oid));
                               return n;
                             })} />
                      <span>(Select All{term ? ' matching' : ''})</span>
                    </label>
                    {list.map(r => (
                      <label key={r.oid} className="crb-role-item">
                        <input type="checkbox" checked={uaSel.has(r.oid)} onChange={() => toggleUa(r.oid)} />
                        <span>{r.name ?? '(unnamed)'}</span>
                      </label>
                    ))}
                  </>
                );
              })()}
            </div>
            <div className="crb-perm-dlg-ftr">
              <span style={{ marginRight: 'auto', fontSize: 12, color: 'var(--ai-muted)' }}>{uaSel.size} selected</span>
              <button className="ca-btn ca-btn-solid" onClick={() => void uaConfirm()} disabled={uaSaving}>
                {uaSaving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} OK
              </button>
              <button className="ca-btn" onClick={() => setUaOpen(false)}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Report Package edit dialog (ALPACA ReportPackage_DetailView) ── */}
      {pkgEditOpen && (
        <div className="crb-overlay" onClick={e => { if (e.target === e.currentTarget) closePkgEdit(); }}>
          <div className={`crb-modal crb-pkg-modal${pkgMax ? ' crb-pkg-modal--max' : ''}`}>
            <div className="crb-modal-hdr">
              <div className="crb-modal-hdr-l">
                <i className="ti ti-package" style={{ fontSize: 16 }} />
                <span className="crb-modal-hdr-title">Report Package</span>
              </div>
              <div className="crb-modal-hdr-btns">
                <button className="crb-modal-close crb-modal-max" title={pkgMax ? 'Restore' : 'Maximize'} onClick={() => setPkgMax(m => !m)}>
                  <i className={`ti ${pkgMax ? 'ti-minimize' : 'ti-maximize'}`} />
                </button>
                <button className="crb-modal-close" onClick={closePkgEdit}>×</button>
              </div>
            </div>

            <div className="crb-modal-body">
              {pkgDetailLoading || !pkgDetail ? (
                <div style={{ padding: 24, color: 'var(--ai-muted)' }}>Loading…</div>
              ) : (
                <>
                  {/* Header: two columns */}
                  <div className="crb-two-col crb-pkg-head">
                    <div className="crb-fields">
                      <Row label="Package Name: *">
                        <input value={pkgDetail.packageName ?? ''} autoFocus
                               onChange={e => pkgField('packageName', e.target.value)} />
                      </Row>
                      <Row label="Client List:">
                        <AiCombo value={pkgDetail.clientOid ?? ''}
                                 options={pkgClients.map(c => ({ value: c.oid, label: c.name ?? '(unnamed)' }))}
                                 onChange={v => pkgField('clientOid', v)}
                                 onNew={openClientNew} newTitle="New client" />
                      </Row>
                      <Row label="">
                        <div className="crb-flags-inline">
                          <Flag label="Is Active" on={pkgDetail.isActive} set={v => pkgField('isActive', v)} />
                          <Flag label="ESign"     on={pkgDetail.eSign}    set={v => pkgField('eSign', v)} />
                        </div>
                      </Row>
                    </div>
                    <div className="crb-fields">
                      <Row label="Level One Signer:">
                        <AiCombo value={pkgDetail.levelOneSigner ?? ''}
                                 options={pkgEmployees.map(em => ({ value: em.oid, label: em.name ?? '(unnamed)' }))}
                                 onChange={v => pkgField('levelOneSigner', v)} />
                      </Row>
                      <Row label="Level Two Signer:">
                        <AiCombo value={pkgDetail.levelTwoSigner ?? ''}
                                 options={pkgEmployees.map(em => ({ value: em.oid, label: em.name ?? '(unnamed)' }))}
                                 onChange={v => pkgField('levelTwoSigner', v)} />
                      </Row>
                      <Row label="Level Three Signer:">
                        <AiCombo value={pkgDetail.levelThreeSigner ?? ''}
                                 options={pkgEmployees.map(em => ({ value: em.oid, label: em.name ?? '(unnamed)' }))}
                                 onChange={v => pkgField('levelThreeSigner', v)} />
                      </Row>
                      {/* User Access on the RIGHT column, under the signers (ALPACA layout) */}
                      <Row label="User Access:">
                        <RoleMultiSelect roles={pkgRoles} selected={pkgRoleIds} onChange={setPkgRoleIds} />
                      </Row>
                    </div>
                  </div>

                  {/* Reports sub-grid */}
                  <div className="crb-perm-section">
                    <div className="crb-perm-title"><i className="ti ti-list-details" /> Reports</div>
                    {/* Shared .ca-btn action bar — ONE solid primary (Add), prototype glyphs. */}
                    <div className="crb-perm-toolbar">
                      <button className="ca-btn ca-btn-solid" onClick={openPkgPicker}><span className="tb-i">＋</span> Add</button>
                      <button className="ca-btn" onClick={() => void pkgRefreshReports()}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
                      <button className="ca-btn" disabled={pkgItemSel.size === 0} onClick={pkgRemoveReport}><span className="tb-i tb-i-red">🗑</span> Remove</button>
                      <ExportToButton options={exportMenu(reportsData, `report-package-${pkgDetail.packageName || 'reports'}`)} />
                    </div>
                    <div className="crb-pkg-grid-wrap">
                    <table className="crb-perm-tbl crb-pkg-items">
                      <thead><tr>
                        <th style={{ width: 30, textAlign: 'center' }}>
                          <input type="checkbox"
                                 checked={pkgDetail.reports.length > 0 && pkgItemSel.size === pkgDetail.reports.length}
                                 ref={el => { if (el) el.indeterminate = pkgItemSel.size > 0 && pkgItemSel.size < pkgDetail.reports.length; }}
                                 onChange={e => setPkgItemSel(e.target.checked ? new Set(pkgDetail.reports.map(r => r.oid)) : new Set())} />
                        </th>
                        {([
                          ['reportName', 'ReportName', {}],
                          ['customCaption', 'CustomCaption', {}],
                          ['displayPage', 'DisplayPage', { width: 90, textAlign: 'center' as const }],
                          ['pageCount', 'IncludePageCount', { width: 110, textAlign: 'center' as const }],
                          ['sort', 'Sort', { width: 70, textAlign: 'center' as const }],
                        ] as [keyof ReportPackageItem, string, CSSProperties][]).map(([col, label, style]) => (
                          <th key={col} className="crb-sortable" style={style} onClick={() => pkgSortBy(col)}>
                            <span className="crb-th-inner">
                              <span className="crb-th-lbl">{label}{pkgSortArrow(col)}</span>
                              <ColFilterButton variant="inline" col={col} label={label} rows={pkgDetail.reports}
                                formatter={r => reportText(r, col)} value={reportFilters[col] ?? null}
                                onChange={v => setReportFilters(f => ({ ...f, [col]: v }))} />
                            </span>
                          </th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {pkgDetail.reports.length === 0 ? (
                          <tr><td colSpan={6} className="crb-perm-empty">No reports — click Add</td></tr>
                        ) : pkgSortedReports(pkgDetail.reports).map(it => (
                          <tr key={it.oid} className={pkgItemSel.has(it.oid) ? 'sel' : ''}>
                            <td style={{ textAlign: 'center' }}>
                              <input type="checkbox" checked={pkgItemSel.has(it.oid)}
                                     onChange={() => setPkgItemSel(prev => {
                                       const n = new Set(prev);
                                       if (n.has(it.oid)) n.delete(it.oid); else n.add(it.oid);
                                       return n;
                                     })} />
                            </td>
                            <td className="crb-pkg-rname">{it.reportName || ''}</td>
                            <td>
                              <input value={it.customCaption ?? ''} onClick={e => e.stopPropagation()}
                                     onChange={e => pkgItemField(it.oid, 'customCaption', e.target.value)} />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input type="checkbox" className="crb-cbx" checked={it.displayPage}
                                     onClick={e => e.stopPropagation()} onChange={e => pkgItemField(it.oid, 'displayPage', e.target.checked)} />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input type="checkbox" className="crb-cbx" checked={it.pageCount}
                                     onClick={e => e.stopPropagation()} onChange={e => pkgItemField(it.oid, 'pageCount', e.target.checked)} />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input type="number" style={{ width: 56 }} value={it.sort} onClick={e => e.stopPropagation()}
                                     onChange={e => pkgItemField(it.oid, 'sort', Number(e.target.value) || 0)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="crb-modal-ftr">
              <button className="ca-btn ca-btn-solid" onClick={savePackage}
                      disabled={pkgDetailLoading || pkgSaving || !pkgDetail?.packageName?.trim()}>
                {pkgSaving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} OK
              </button>
              <button className="ca-btn" onClick={closePkgEdit}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add-report picker (ALPACA: searchable multi-select of report names) ── */}
      {pkgEditOpen && pkgPickerOpen && (
        <div className="crb-overlay2" onClick={e => { if (e.target === e.currentTarget) setPkgPickerOpen(false); }}>
          <div className="crb-pick-dialog">
            <div className="crb-perm-dlg-hdr">
              <span>Add Reports</span>
              <button className="crb-modal-close" onClick={() => setPkgPickerOpen(false)}>×</button>
            </div>
            <div className="crb-pick-search">
              {/* shared .list-search (boStandard.css) — never a bespoke search box */}
              <div className="list-search">
                <span className="tb-i">🔍</span>
                <input placeholder="Text to search…" value={pkgPickerSearch} autoFocus
                       onChange={e => setPkgPickerSearch(e.target.value)} />
              </div>
            </div>
            <div className="crb-pick-list">
              {(() => {
                const term = pkgPickerSearch.trim().toLowerCase();
                const names = term ? pkgReportNames.filter(n => n.toLowerCase().includes(term)) : pkgReportNames;
                if (names.length === 0) return <div className="crb-muted" style={{ padding: 12 }}>No reports found</div>;
                const allChecked = names.length > 0 && names.every(n => pkgPickerSel.has(n));
                return (
                  <>
                    <label className="crb-role-item crb-role-all">
                      <input type="checkbox" checked={allChecked}
                             ref={el => { if (el) el.indeterminate = pkgPickerSel.size > 0 && !allChecked; }}
                             onChange={() => setPkgPickerSel(prev => {
                               const n = new Set(prev);
                               if (allChecked) names.forEach(x => n.delete(x)); else names.forEach(x => n.add(x));
                               return n;
                             })} />
                      <span>(Select All{term ? ' matching' : ''})</span>
                    </label>
                    {names.map(n => (
                      <label key={n} className="crb-role-item">
                        <input type="checkbox" checked={pkgPickerSel.has(n)} onChange={() => togglePicker(n)} />
                        <span>{n}</span>
                      </label>
                    ))}
                  </>
                );
              })()}
            </div>
            <div className="crb-perm-dlg-ftr">
              <span style={{ marginRight: 'auto', fontSize: 12, color: 'var(--ai-muted)' }}>{pkgPickerSel.size} selected</span>
              <button className="ca-btn ca-btn-solid" onClick={pkgPickerConfirm} disabled={pkgPickerSel.size === 0}>
                <span className="tb-i">＋</span> OK
              </button>
              <button className="ca-btn" onClick={() => setPkgPickerOpen(false)}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Styled name prompt (New Form / Category / Report Type) — replaces
            the native window.prompt; dims + blurs the background behind it ── */}
      {promptCfg && (
        <div className="crb-prompt-overlay"
             onClick={e => { if (e.target === e.currentTarget) closePrompt(null); }}>
          <div className="crb-prompt-dialog">
            <div className="crb-prompt-hdr">{promptCfg.title}</div>
            <div className="crb-prompt-body">
              <div className="crb-prompt-field">
                <label className="crb-prompt-lbl">{promptCfg.label}: <span className="crb-req">*</span></label>
                <input className="crb-prompt-input" autoFocus value={promptValue}
                       onChange={e => setPromptValue(e.target.value)}
                       onKeyDown={e => {
                         if (e.key === 'Enter' && promptValue.trim()) closePrompt({ name: promptValue, description: promptDesc });
                         else if (e.key === 'Escape') closePrompt(null);
                       }} />
              </div>
              {promptCfg.withDescription && (
                <div className="crb-prompt-field">
                  <label className="crb-prompt-lbl">Description:</label>
                  <textarea className="crb-prompt-input" rows={3} value={promptDesc}
                            onChange={e => setPromptDesc(e.target.value)} />
                </div>
              )}
            </div>
            <div className="crb-prompt-ftr">
              <button className="ca-btn ca-btn-solid" disabled={!promptValue.trim()}
                      onClick={() => closePrompt({ name: promptValue, description: promptDesc })}>
                <span className="tb-i">＋</span> OK
              </button>
              <button className="ca-btn" onClick={() => closePrompt(null)}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Styled confirm dialog (Delete actions) — readable, over a blurred bg ── */}
      {confirmCfg && (
        <div className="crb-prompt-overlay"
             onClick={e => { if (e.target === e.currentTarget) closeConfirm(false); }}>
          <div className="crb-prompt-dialog">
            <div className="crb-prompt-hdr">{confirmCfg.title}</div>
            <div className="crb-prompt-body">
              <p className="crb-confirm-msg">{confirmCfg.message}</p>
            </div>
            <div className="crb-prompt-ftr">
              <button className="ca-btn ca-btn-solid" autoFocus onClick={() => closeConfirm(true)}>
                <span className="tb-i">🗑</span> OK
              </button>
              <button className="ca-btn" onClick={() => closeConfirm(false)}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New Client popup (Client List "New" — ALPACA NwClient) ── */}
      {clientNewOpen && (
        <div className="crb-prompt-overlay" onClick={e => { if (e.target === e.currentTarget) setClientNewOpen(false); }}>
          <div className="crb-prompt-dialog" style={{ width: 460 }}>
            <div className="crb-prompt-hdr">New Client</div>
            <div className="crb-prompt-body">
              <div className="crb-prompt-field">
                <label className="crb-prompt-lbl">Client Name: <span className="crb-req">*</span></label>
                <input className="crb-prompt-input" autoFocus value={clientNewName}
                       onChange={e => setClientNewName(e.target.value)}
                       onKeyDown={e => {
                         if (e.key === 'Enter' && clientNewName.trim()) void saveClientNew();
                         else if (e.key === 'Escape') setClientNewOpen(false);
                       }} />
              </div>
              <div className="crb-prompt-field">
                <label className="crb-prompt-lbl">Client Unique ID:</label>
                <input className="crb-prompt-input" value="(auto-assigned on save)" readOnly
                       style={{ background: 'var(--ai-readonly-bg)', color: 'var(--ai-muted)' }} />
              </div>
            </div>
            <div className="crb-prompt-ftr">
              <button className="ca-btn ca-btn-solid" disabled={clientNewSaving || !clientNewName.trim()} onClick={() => void saveClientNew()}>
                {clientNewSaving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">＋</span>} OK
              </button>
              <button className="ca-btn" onClick={() => setClientNewOpen(false)}>
                <span className="tb-i tb-i-grey">↩︎</span> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Small helpers ────────────────────────────────────────────────────────────
function Row({ label, children }: { label: string; children: ReactNode }) {
  // A trailing "*" marks a required field — render it in the standard error-red
  // instead of inline plain text (e.g. "Package Name: *").
  const required = label.trimEnd().endsWith('*');
  const text = required ? label.replace(/\s*\*\s*$/, '') : label;
  return (
    <div className="crb-row">
      <label className="crb-row-lbl">{text}{required && <span className="crb-req-star">*</span>}</label>
      <div className="crb-row-ctl">{children}</div>
    </div>
  );
}
function Flag({ label, on, set }: { label: string; on: boolean; set: (v: boolean) => void }) {
  return (
    <label className="crb-flag">
      <input type="checkbox" checked={on} onChange={e => set(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/** "Export to" button + format dropdown (PDF / Excel / CSV) for sub-grid toolbars
 *  that aren't the shared AppToolbar.  Reuses the AppToolbar export-menu styles. */
function ExportToButton({ options }: { options: ExportOption[] }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="app-toolbar__export">
      <button className="ca-btn" onClick={() => setOpen(o => !o)}>
        <span className="tb-i tb-i-teal">📤</span> Export to ▾
      </button>
      {open && (
        <>
          <div className="app-toolbar__export-backdrop" onClick={() => setOpen(false)} />
          <div className="app-toolbar__export-menu" role="menu">
            {options.map(opt => (
              <button key={opt.label} className="app-toolbar__export-item" role="menuitem"
                      onClick={() => { setOpen(false); opt.onSelect(); }}>
                <i className={`ti ${opt.icon ?? 'ti-file-export'}`} /> {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

/** Collapsed multi-select dropdown for roles (User Access) — opens on click,
 *  closes on outside click; shows selected role names, includes Select all.
 *  Replaces the always-expanded checkbox list (ALPACA parity). */
function RoleMultiSelect({ roles, selected, onChange, placeholder = 'N/A' }: {
  roles: LookupOption[]; selected: Set<string>; onChange: (next: Set<string>) => void; placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLButtonElement>(null);
  // Position the popup with FIXED coords from the field rect so it escapes any
  // overflow:auto ancestor (the grid scroll box / dialog body) and never clips.
  const place = useCallback(() => {
    const el = fieldRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, 220);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    setPos({ left, top: r.bottom + 2, width });
  }, []);
  useEffect(() => {
    if (!open) return;
    place();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    // Reposition (don't close) while the popup itself is scrolled; only close
    // when an ANCESTOR page/dialog scrolls so the field has moved away.
    const onScroll = (e: Event) => {
      if (popRef.current && popRef.current.contains(e.target as Node)) return;  // internal list scroll — keep open
      place();
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, place]);
  const names = roles.filter(r => selected.has(r.oid)).map(r => r.name ?? '').filter(Boolean).join('; ');
  const allChecked = roles.length > 0 && roles.every(r => selected.has(r.oid));
  const toggleOne = (oid: string) => { const n = new Set(selected); if (n.has(oid)) n.delete(oid); else n.add(oid); onChange(n); };
  return (
    <div className="crb-ms" ref={ref}>
      <button ref={fieldRef} type="button" className="crb-ms-field" onClick={() => setOpen(o => !o)}>
        <span className={names ? '' : 'crb-muted'}>{names || placeholder}</span>
        <i className="ti ti-chevron-down" />
      </button>
      {open && pos && (
        <div className="crb-ms-pop" ref={popRef}
             style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width }}>
          {roles.length === 0 ? <div className="crb-muted" style={{ padding: 8 }}>No roles available</div> : (
            <>
              <label className="crb-role-item crb-role-all">
                <input type="checkbox" checked={allChecked}
                       ref={el => { if (el) el.indeterminate = selected.size > 0 && !allChecked; }}
                       onChange={() => onChange(allChecked ? new Set() : new Set(roles.map(r => r.oid)))} />
                <span>(Select All)</span>
              </label>
              {roles.map(r => (
                <label key={r.oid} className="crb-role-item">
                  <input type="checkbox" checked={selected.has(r.oid)} onChange={() => toggleOne(r.oid)} />
                  <span>{r.name ?? '(unnamed)'}</span>
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const crbStyles = `
  /* Structural greys tokenized per BO UI Standard v2 (tokens.css --ai-*);
     brand/active colours run through the --brand-accent chain. */
  .crb-tabs { display:flex; gap:2px; padding:6px 10px 0; background:var(--ai-header-bg); border-bottom:1px solid var(--ai-line); }
  .crb-tab {
    border:1px solid transparent; border-bottom:none; background:transparent;
    padding:7px 16px; font-size:13px; font-weight:600; color:var(--ai-muted); cursor:pointer;
    border-radius:6px 6px 0 0; display:inline-flex; align-items:center; gap:6px;
  }
  .crb-tab:hover { color:var(--brand-accent-hover, #c04a0a); }
  .crb-tab.active { color:var(--brand-accent-hover, #c04a0a); background:#fff; border-color:var(--ai-line); }
  .crb-sel-pill {
    display:inline-flex; align-items:center; gap:6px;
    background:color-mix(in srgb, var(--brand-accent, #e8670a) 10%, #fff); color:var(--brand-accent-hover, #c04a0a);
    border:1px solid color-mix(in srgb, var(--brand-accent, #e8670a) 30%, #fff);
    padding:3px 4px 3px 10px; border-radius:12px; font-size:11px; font-weight:600;
  }
  .crb-sel-clear { background:transparent; border:none; color:var(--brand-accent-hover, #c04a0a); cursor:pointer; font-size:14px; line-height:1; padding:0 6px; }
  .crb-sel-clear:hover { color:var(--ai-orange-dark); }

  .crb-overlay { position:fixed; inset:0; background:rgba(0,0,0,.55); display:flex; align-items:center; justify-content:center; z-index:9000; backdrop-filter:blur(2px); }
  .crb-modal { width:min(96vw, 860px); max-height:94vh; background:#fff; border-radius:10px; overflow:hidden; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.4); }
  .crb-pkg-modal { width:min(97vw, 960px); }
  .crb-pkg-modal.crb-pkg-modal--max { width:98vw; max-width:98vw; height:96vh; max-height:96vh; }
  .crb-pkg-modal.crb-pkg-modal--max .crb-modal-body { flex:1 1 auto; }
  .crb-modal.crb-modal--max { width:98vw !important; max-width:98vw; height:96vh; max-height:96vh; }
  .crb-modal.crb-modal--max .crb-modal-body { flex:1 1 auto; }
  /* Add-report picker dialog */
  .crb-pick-dialog { width:min(94vw, 560px); max-height:86vh; background:#fff; border-radius:10px; display:flex; flex-direction:column; box-shadow:0 18px 50px rgba(0,0,0,.45); overflow:hidden; }
  /* Search row hosts the SHARED .list-search (boStandard.css) at full width. */
  .crb-pick-search { padding:10px 14px; border-bottom:1px solid var(--ai-line-soft); }
  .crb-pick-search .list-search { width:100%; }
  .crb-pick-list { flex:1; overflow-y:auto; padding:4px; min-height:200px; }
  .crb-sortable { cursor:pointer; user-select:none; white-space:nowrap; }
  .crb-sortable:hover { background:var(--ai-row-hover); }
  /* Sort indicator — light grey, normal weight (grid standard §18.6). */
  .crb-sort-ind { font-size:13px; margin-left:4px; vertical-align:middle; color:var(--ai-muted); }
  .crb-sort-ind.dim { color:var(--ai-muted); opacity:.55; }
  .crb-pkg-items td { padding:3px 6px; }
  /* Flat inline-edit cells — borderless until hover/focus (LIMS look, no highlight box). */
  .crb-pkg-items select, .crb-pkg-items input[type=text], .crb-pkg-items input:not([type]) { width:100%; font-size:12.5px; padding:4px 6px; border:1px solid transparent; border-radius:3px; box-sizing:border-box; background:transparent; }
  .crb-pkg-items input[type=number] { font-size:12.5px; padding:4px 6px; border:1px solid transparent; border-radius:3px; background:transparent; }
  .crb-pkg-items input:hover, .crb-pkg-items select:hover { border-color:var(--ai-line); }
  .crb-pkg-items input:focus, .crb-pkg-items select:focus { outline:none; border-color:var(--ai-muted); background:#fff; box-shadow:none; }
  /* LIGHT modal header (BO UI Standard §11 — old fixed-blue gradient removed),
     red round close, quiet grey maximize chip. */
  .crb-modal-hdr { display:flex; align-items:center; justify-content:space-between; padding:12px 18px; background:var(--ai-header-bg); color:var(--ai-ink); border-bottom:1px solid var(--ai-line); }
  .crb-modal-hdr-l { display:flex; align-items:center; gap:8px; }
  .crb-modal-hdr-title { font-size:13px; font-weight:800; }
  .crb-modal-close { width:26px; height:26px; border:none; border-radius:50%; background:var(--ai-pill-alert-bg); color:var(--ai-pill-alert-fg); cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:15px; }
  .crb-modal-close:hover { background:var(--ai-red); color:#fff; }
  .crb-modal-hdr-btns { display:flex; align-items:center; gap:6px; }
  .crb-modal-max { background:var(--ai-row-hover); color:var(--ai-muted); font-size:14px; }
  .crb-modal-max i { color:inherit; }
  .crb-modal-max:hover { background:var(--ai-line); color:var(--ai-ink); }
  .crb-modal-body { padding:18px 22px; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:16px; }

  .crb-two-col { display:grid; grid-template-columns:minmax(0,1.4fr) minmax(180px,0.9fr); gap:24px; }
  @media (max-width:680px){ .crb-two-col { grid-template-columns:1fr; } }
  .crb-fields { display:flex; flex-direction:column; gap:8px; }
  .crb-row { display:grid; grid-template-columns:110px 1fr; align-items:start; gap:8px; }
  .crb-row-lbl { font-size:12.5px; color:var(--ai-ink); padding-top:6px; }
  .crb-row-ctl input, .crb-row-ctl select, .crb-row-ctl textarea {
    width:100%; font-size:13px; padding:5px 8px; border:1px solid var(--ai-line); border-radius:3px;
    background:#fff; color:var(--ai-ink); outline:none; box-sizing:border-box;
  }
  .crb-row-ctl input:focus, .crb-row-ctl select:focus, .crb-row-ctl textarea:focus { border-color:var(--brand-accent, #e8670a); }
  .crb-ro { background:var(--ai-readonly-bg) !important; color:var(--ai-muted) !important; }
  /* Category / Report Type / signer / client lookups now use the shared
     .ai-lkbox primitive (boStandard.css) so the plus/clear sit INSIDE the field
     box like XAF — the old .crb-lookup-row / .crb-lk-btn external-button CSS was
     removed with that change. The in-grid inline editors use .ai-lkbox-sm. */
  .crb-flags { display:flex; flex-direction:column; gap:11px; padding-top:4px; }
  /* editable checkbox in the boolean grid columns while inline-editing
     (read-only display uses the shared .cellcheck from boStandard.css) */
  .crb-cbx { width:15px; height:15px; accent-color: var(--brand-accent, #e8670a); cursor:default; margin:0; vertical-align:middle; }
  .crb-flag { display:inline-flex; align-items:center; gap:7px; font-size:13px; color:var(--ai-ink); cursor:pointer; }

  .crb-perm-section { border-top:1px solid var(--ai-line-soft); padding-top:12px; }
  .crb-perm-title { font-size:15px; font-weight:600; color:var(--ai-ink); margin-bottom:8px; }
  /* Sub-grid action bars host shared .ca-btn buttons (boStandard.css). */
  .crb-perm-toolbar { display:flex; gap:6px; padding:6px 8px; background:var(--ai-header-bg); border:1px solid var(--ai-line); border-bottom:none; border-radius:4px 4px 0 0; }
  /* Sub-grid tables — grey header, ink cells, grey hover, NO zebra/orange rows
     (BO UI Standard §9). */
  .crb-perm-tbl { width:100%; border-collapse:collapse; font-size:12.5px; }
  .crb-perm-tbl th { text-align:left; background:var(--ai-header-bg); color:var(--ai-ink); font-weight:800; font-size:12px; padding:11px 12px; border:1px solid var(--ai-line); }
  .crb-perm-tbl td { padding:10px 12px; border:1px solid var(--ai-line-soft); color:var(--ai-ink); }
  .crb-perm-tbl tbody tr { cursor:pointer; }
  .crb-perm-tbl tbody tr:hover { background:var(--ai-row-hover); }
  .crb-perm-tbl tbody tr.sel { background:var(--ai-row-selected); }
  .crb-perm-empty { text-align:center; color:var(--ai-muted); font-style:italic; padding:18px !important; cursor:default; }
  .crb-muted { color:var(--ai-muted); font-style:italic; }
  .crb-updated { font-size:11px; color:var(--ai-muted); }

  /* per-row edit pencil in the main grid (Tabler icon buttons — icon tokens) */
  .crb-row-edit { background:transparent; border:none; cursor:pointer; color:var(--brand-accent-hover, #c04a0a); padding:2px 4px; border-radius:3px; font-size:14px; line-height:1; }
  .crb-row-edit:hover { background:color-mix(in srgb, var(--brand-accent, #e8670a) 10%, #fff); }
  .crb-row-edit.save { color:var(--ai-icon-new); }
  .crb-row-edit.save:hover { background:var(--ai-row-hover); }
  .crb-row-edit.cancel { color:var(--ai-muted); }
  .crb-row-edit.cancel:hover { background:var(--ai-row-hover); }
  .crb-row-edit:disabled { opacity:.5; cursor:not-allowed; }
  .crb-row-edit.screen { color:var(--ai-icon-edit); }
  .crb-row-edit.screen:hover { background:var(--ai-row-hover); }
  .crb-row-edit.design { color:var(--brand-accent-hover, #c04a0a); }
  .crb-row-edit.design:hover { background:var(--ai-row-hover); }
  .crb-inline-actions { display:inline-flex; gap:1px; }
  /* inline-edit inputs inside a grid cell — the editing tint maps to the shared
     required-field tokens (yellow tint family). */
  .crb-inline-inp { width:100%; box-sizing:border-box; font-size:12px; padding:2px 4px; border:1px solid var(--ai-required-line); border-radius:3px; background:var(--ai-required-bg); outline:none; }
  .crb-inline-inp:focus { border-color:var(--brand-accent, #e8670a); }
  /* inline Category / Report Type editor now uses the shared .ai-lkbox.ai-lkbox-sm
     (boStandard.css) so the ＋/clear sit inside the cell's dropdown box. */

  /* Report Designer grid: clear SINGLE-LINE headers (no wrapping / clipping);
     columns size to their content + header and the grid scrolls horizontally
     when needed (user preference 2026-06-18 — readable headers over no-scroll). */
  .crb-designer-grid .adg-table thead th { white-space: nowrap; }
  .crb-designer-grid .adg-th-label { white-space: nowrap; overflow: visible; text-overflow: clip; }

  /* User Access click-to-open multi-select dropdown */
  .crb-ms { position:relative; width:100%; }
  .crb-ms-field { width:100%; display:flex; align-items:center; justify-content:space-between; gap:6px; font-size:13px; padding:6px 8px; border:1px solid var(--ai-line); border-radius:4px; background:#fff; color:var(--ai-ink); cursor:pointer; text-align:left; box-sizing:border-box; }
  .crb-ms-field:hover { border-color: var(--brand-accent, #e8670a); }
  .crb-ms-field > span { flex:1 1 auto; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .crb-ms-field i { flex:0 0 auto; color:var(--ai-muted); }
  /* popup is positioned FIXED inline (escapes overflow); high z-index over dialogs+grid */
  .crb-ms-pop { z-index:9999; max-height:240px; overflow-y:auto; background:#fff; border:1px solid var(--ai-line); border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,.22); padding:4px; }

  /* Styled name-prompt modal — replaces window.prompt; sits above every other
     dialog and BLURS the background behind it (ALPACA-style centred popup). */
  .crb-prompt-overlay { position:fixed; inset:0; z-index:12000; display:flex; align-items:center; justify-content:center;
                        background:rgba(20,30,48,.45); backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px); }
  .crb-prompt-dialog { width:380px; max-width:92vw; background:#fff; border-radius:10px; overflow:hidden;
                       box-shadow:0 22px 60px rgba(0,0,0,.4); animation:crbPromptIn .12s ease-out; }
  @keyframes crbPromptIn { from { transform:translateY(-8px); opacity:.6; } to { transform:none; opacity:1; } }
  /* Light dialog header (BO UI Standard §11 — old fixed blue removed). */
  .crb-prompt-hdr { background:var(--ai-header-bg); color:var(--ai-ink); font-weight:800; font-size:13px; padding:12px 16px; border-bottom:1px solid var(--ai-line); }
  .crb-prompt-body { padding:18px 16px 8px; display:flex; flex-direction:column; gap:14px; }
  .crb-prompt-field { display:flex; flex-direction:column; }
  .crb-prompt-lbl { display:block; font-size:12px; color:var(--ai-muted); margin-bottom:6px; }
  .crb-req { color:var(--ai-red); }
  .crb-prompt-input { width:100%; box-sizing:border-box; padding:9px 11px; border:1px solid var(--ai-line); border-radius:6px; font-size:14px; font-family:inherit; resize:vertical; }
  .crb-prompt-input:focus { border-color:var(--brand-accent, #e8670a); outline:none; box-shadow:0 0 0 3px color-mix(in srgb, var(--brand-accent, #e8670a) 14%, transparent); }
  .crb-confirm-msg { margin:0; font-size:13.5px; color:var(--ai-ink); line-height:1.55; }
  .crb-prompt-ftr { display:flex; justify-content:flex-end; gap:8px; padding:14px 16px 16px; }

  /* Form Report Permission dialog (nested on top of the editor modal) */
  .crb-overlay2 { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:9200; }
  /* Sit near the TOP (not vertically centred) so the Forms <select> has room
     below it and its dropdown opens DOWNWARD instead of covering the dialog. */
  .crb-perm-dialog { width:min(94vw, 560px); background:#fff; border-radius:10px; display:flex; flex-direction:column; box-shadow:0 18px 50px rgba(0,0,0,.45); align-self:flex-start; margin-top:7vh; }
  /* Light dialog header (was the theme-orange band — BO UI Standard §11). */
  .crb-perm-dlg-hdr { display:flex; align-items:center; justify-content:space-between; padding:10px 16px; background:var(--ai-header-bg); color:var(--ai-ink); font-size:13px; font-weight:800; border-bottom:1px solid var(--ai-line); border-radius:10px 10px 0 0; }
  .crb-perm-dlg-body { padding:20px 24px; display:flex; flex-direction:column; gap:16px; overflow:visible; min-height:140px; }
  /* "Assign Users" link shown in the Form Report Permissions sub-grid (ALPACA). */
  .crb-assign-link { color:var(--brand-accent-hover, #c04a0a); cursor:pointer; text-decoration:underline; }
  .crb-assign-link:hover { color:var(--ai-orange-dark); }
  .crb-role-list { display:flex; flex-direction:column; gap:2px; max-height:240px; overflow-y:auto; border:1px solid var(--ai-line); border-radius:4px; padding:4px; background:var(--ai-header-bg); }
  /* Force a clean [checkbox] [label] left-aligned row — applies in BOTH the
     inline list (.crb-role-list) and the dropdown popup (.crb-ms-pop), overriding
     any global label CSS that would spread checkbox + label apart. */
  .crb-role-item { display:flex !important; flex-direction:row !important; align-items:center !important; justify-content:flex-start !important; gap:8px !important; width:100%; margin:0; padding:5px 8px; font-size:13px; color:var(--ai-ink); cursor:pointer; text-align:left; border-radius:3px; box-sizing:border-box; }
  .crb-role-item:hover { background:var(--ai-row-hover); }
  .crb-role-item input { flex:0 0 auto; width:15px; height:15px; margin:0; accent-color: var(--brand-accent, #e8670a); order:0; }
  .crb-role-item span { flex:1 1 auto; text-align:left; line-height:1.3; order:1; }
  .crb-role-all { border-bottom:1px solid var(--ai-line); font-weight:600 !important; color:var(--ai-ink) !important; position:sticky; top:0; background:var(--ai-header-bg); z-index:1; }
  .crb-perm-dlg-ftr { display:flex; justify-content:flex-end; gap:8px; padding:10px 16px; background:var(--ai-header-bg); border-top:1px solid var(--ai-line); }

  /* Footers host shared .ca-btn buttons (boStandard.css) — the old bespoke
     .crb-btn variants are removed. */
  .crb-modal-ftr { display:flex; align-items:center; justify-content:flex-end; gap:8px; padding:10px 18px; background:var(--ai-header-bg); border-top:1px solid var(--ai-line); }

  /* ── Report Package dialog: polished header form ───────────────────────── */
  /* Group the header fields into a clean white card so the dialog reads as two
     tidy field groups rather than a flat wall of inputs. */
  .crb-pkg-head {
    background:#fff; border:1px solid var(--ai-line); border-radius:10px;
    padding:22px 26px; column-gap:52px; align-items:start;
    box-shadow:var(--ai-shadow-card);
    /* Even left/right columns (was 1.4fr / 0.9fr). */
    grid-template-columns:minmax(0,1fr) minmax(0,1fr);
  }
  /* Wider label column + nowrap so "Level Three Signer:" stays on one line,
     and vertically centre each label against its single-line control. */
  .crb-pkg-head .crb-row { grid-template-columns:130px 1fr; align-items:center; gap:12px; }
  .crb-pkg-head .crb-row-lbl { white-space:nowrap; padding-top:0; font-weight:600; font-size:12.5px; color:var(--ai-muted); }
  .crb-pkg-head .crb-fields { gap:15px; }
  .crb-pkg-head .crb-row-ctl input,
  .crb-pkg-head .crb-row-ctl select { height:34px; border-radius:6px; border-color:var(--ai-line); }
  .crb-pkg-head .crb-row-ctl input:hover,
  .crb-pkg-head .crb-row-ctl select:hover { border-color:var(--ai-muted); }
  .crb-pkg-head .crb-row-ctl input:focus,
  .crb-pkg-head .crb-row-ctl select:focus { border-color:var(--brand-accent, #e8670a); box-shadow:0 0 0 3px color-mix(in srgb, var(--brand-accent, #e8670a) 14%, transparent); }
  /* Required-field asterisk in the standard error-red. */
  .crb-req-star { color:var(--ai-red); font-weight:700; margin-left:2px; }
  /* Is Active / ESign side-by-side, aligned under the inputs (no magic margin) */
  .crb-flags-inline { display:flex; align-items:center; gap:28px; height:34px; }
  .crb-pkg-head .crb-flag { font-weight:500; color:var(--ai-ink); }
  .crb-pkg-head .crb-flag input { width:16px; height:16px; accent-color: var(--brand-accent, #e8670a); }

  /* "Reports" sub-grid heading — small icon + label above the toolbar. */
  .crb-perm-section .crb-perm-title {
    display:flex; align-items:center; gap:7px;
    font-size:14px; font-weight:700; color:var(--ai-ink); margin:0 0 10px;
  }
  .crb-perm-section .crb-perm-title i { font-size:17px; color:var(--ai-muted); }
  /* The title now provides the visual break, so drop the hard top rule. */
  .crb-pkg-modal .crb-perm-section { border-top:none; padding-top:2px; }

  /* Reports grid as one rounded, bordered card (toolbar strip + table). */
  .crb-pkg-modal .crb-perm-toolbar {
    border:1px solid var(--ai-line); border-bottom:none; border-radius:10px 10px 0 0;
    background:var(--ai-header-bg); padding:9px 12px; gap:8px;
  }
  .crb-pkg-grid-wrap {
    border:1px solid var(--ai-line); border-top:none; border-radius:0 0 10px 10px; overflow:hidden;
  }
  /* Reports grid HEADER — flat clean-grey header (BO UI Standard §9; the old
     blue-grey gradient band removed 2026-07-02). */
  .crb-pkg-modal .crb-pkg-items thead th {
    background:var(--ai-header-bg) !important;
    color:var(--ai-ink) !important;
    border-color:var(--ai-line) !important;
    border-bottom:2px solid var(--ai-line) !important;
    font-weight:800; font-size:12px; letter-spacing:.015em;
    padding:11px 12px; vertical-align:middle; white-space:nowrap;
  }
  .crb-pkg-modal .crb-pkg-items .crb-sortable:hover { background:var(--ai-row-hover) !important; }
  /* Label + sort arrow stay together on the left; filter chevron snaps right. */
  .crb-pkg-modal .crb-pkg-items thead th .crb-th-inner { display:flex; align-items:center; gap:6px; width:100%; }
  .crb-pkg-modal .crb-pkg-items thead th .crb-th-lbl { display:inline-flex; align-items:center; gap:5px; }
  .crb-pkg-modal .crb-pkg-items thead th .col-flt-chevron { margin-left:auto; }
  /* Centre-aligned columns keep their label centred, chevron still right. */
  .crb-pkg-modal .crb-pkg-items thead th[style*="center"] .crb-th-inner { justify-content:center; }
  .crb-pkg-modal .crb-pkg-items thead th[style*="center"] .col-flt-chevron { margin-left:6px; }
  /* Active sort arrow in the theme orange; idle arrows soft grey. */
  .crb-pkg-modal .crb-pkg-items .crb-sort-ind { color:var(--brand-accent-hover, #c04a0a); }
  .crb-pkg-modal .crb-pkg-items .crb-sort-ind.dim { color:var(--ai-muted); opacity:.55; }
  /* Filter chevron — quiet by default, clearer on header hover, accent when active. */
  .crb-pkg-modal .crb-pkg-items thead th .col-flt-chevron { color:var(--ai-muted); opacity:.6; transition:opacity .12s ease, color .12s ease; }
  .crb-pkg-modal .crb-pkg-items thead th:hover .col-flt-chevron { opacity:1; color:var(--ai-ink); }
  .crb-pkg-modal .crb-pkg-items thead th .col-flt-chevron.active { color:var(--brand-accent-hover, #c04a0a); opacity:1; }
  /* Select-all checkbox header cell. */
  .crb-pkg-modal .crb-pkg-items thead th:first-child { background:var(--ai-header-bg) !important; }
  .crb-pkg-modal .crb-pkg-items thead th input[type=checkbox] { accent-color: var(--brand-accent, #e8670a); }
  /* Rows — ink cells, grey hover, token selection, NO zebra/orange rows. */
  .crb-pkg-modal .crb-pkg-items td { padding:10px 12px; border-color:var(--ai-line-soft); color:var(--ai-ink); }
  .crb-pkg-modal .crb-pkg-items tbody tr:hover { background:var(--ai-row-hover); }
  .crb-pkg-modal .crb-pkg-items tbody tr.sel { background:var(--ai-row-selected); }
  .crb-pkg-modal .crb-pkg-items .crb-pkg-rname { font-weight:600; color:var(--ai-ink); }
  .crb-pkg-modal .crb-pkg-items input[type=checkbox] { width:16px; height:16px; accent-color: var(--brand-accent, #e8670a); cursor:pointer; }
  .crb-pkg-modal .crb-pkg-items input[type=number] { text-align:center; }
`;
